import React, { useCallback, useEffect, useState, useRef, Fragment } from 'react';
import { motion } from 'framer-motion';
import { SunIcon, MoonIcon, StarIcon, SparklesIcon, CalendarIcon, ClockIcon, MapPinIcon, HeartIcon, BriefcaseIcon, CompassIcon, ShieldIcon, ZapIcon, TrendingUpIcon, BookOpenIcon, TargetIcon, CheckIcon, ActivityIcon, LayersIcon, UsersIcon, AlertTriangleIcon, MessageCircleIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { SOLAR_INTRO, SOLAR_YEAR_THEME, SOLAR_ASCENDANT, SOLAR_SUN_POSITION, SOLAR_MOON, SOLAR_PLANETS_IN_HOUSES, SOLAR_ASPECTS, SOLAR_LIFE_AREAS, SOLAR_RECOMMENDATIONS, SOLAR_SUMMARY } from '../../data/solarReportContent';
import { COMPAT_INTRO, COMPAT_OVERALL_DYNAMICS, COMPAT_SUN_INTERACTION, COMPAT_EMOTIONAL, COMPAT_ROMANTIC, COMPAT_INTELLECTUAL, COMPAT_KARMIC, COMPAT_PLANETS_IN_HOUSES, COMPAT_ASPECTS_DETAILED, COMPAT_STRENGTHS, COMPAT_CONFLICTS, COMPAT_LONGTERM, COMPAT_RECOMMENDATIONS, COMPAT_SUMMARY } from '../../data/compatibilityReportContent';
import { COMBO_NATAL_INTRO, COMBO_PERSONALITY, COMBO_SUN, COMBO_MOON, COMBO_ASCENDANT, COMBO_PLANETS_IN_SIGNS, COMBO_HOUSES_SUMMARY, COMBO_NATAL_ASPECTS, COMBO_NATAL_LIFE_AREAS, COMBO_SOLAR_INTRO, COMBO_YEAR_ENERGY, COMBO_SOLAR_ASCENDANT, COMBO_SOLAR_SUN, COMBO_SOLAR_MOON, COMBO_SOLAR_PLANETS, COMBO_SOLAR_ASPECTS, COMBO_KEY_PERIODS, COMBO_SOLAR_NATAL_INTERACTION, COMBO_YEAR_LIFE_AREAS, COMBO_RECOMMENDATIONS, COMBO_FINAL_SUMMARY } from '../../data/comboReportContent';
import { FORECAST_INTRO, FORECAST_PERIOD_ENERGY, FORECAST_SLOW_TRANSITS, FORECAST_PERSONAL_TRANSITS, FORECAST_HOUSES_ACTIVATION, FORECAST_TRANSIT_ASPECTS, FORECAST_KEY_PERIODS, FORECAST_LIFE_AREAS, FORECAST_OPPORTUNITIES, FORECAST_DIFFICULTIES, FORECAST_RECOMMENDATIONS, FORECAST_SUMMARY, FORECAST_MONTHLY_DETAIL } from '../../data/forecastReportContent';
import { INTRO_CONTENT, EXTENDED_SUN, EXTENDED_MOON, EXTENDED_MERCURY, EXTENDED_VENUS, EXTENDED_MARS, EXTENDED_JUPITER, EXTENDED_SATURN, OUTER_PLANETS_EXTENDED, HOUSES_DETAILED, ASPECTS_DETAILED, HEALTH_SECTION, KARMIC_SECTION, TALENTS_SECTION, CHALLENGES_SECTION, FORECAST_SECTION, FINAL_ANALYSIS, PLANET_POSITIONS_TABLE, ENERGY_RADAR_DATA, ZODIAC_COMPATIBILITY, PERSONAL_AFFIRMATIONS, CURRENT_TRANSITS, CELEBRITY_MATCHES } from '../../data/fullReportContent';
type ServiceType = 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast';
interface PremiumReportPreviewProps {
  serviceType: ServiceType;
}
// ═══════════════════════════════════════════
// SERVICE-SPECIFIC DATA
// ═══════════════════════════════════════════
const SERVICE_CONFIG: Record<ServiceType, {
  title: string;
  subtitle: string;
  gradient: string;
  accentColor: string;
  badgeClass: string;
  icon: React.ReactNode;
  person2?: {
    name: string;
    date: string;
    time: string;
    place: string;
  };
  extraInfo?: string;
}> = {
  natal: {
    title: 'Натальная карта',
    subtitle: 'Полный астрологический разбор личности',
    gradient: 'from-slate-900 via-indigo-900 to-slate-900',
    accentColor: 'indigo',
    badgeClass: 'bg-indigo-500/30 text-indigo-200 border-indigo-400/30',
    icon: <StarIcon className="w-8 h-8" data-id="element-14368" />
  },
  solar: {
    title: 'Соляр (Прогноз на год)',
    subtitle: 'Персональный астрологический прогноз на 2026 год',
    gradient: 'from-slate-900 via-amber-900 to-slate-900',
    accentColor: 'amber',
    badgeClass: 'bg-amber-500/30 text-amber-200 border-amber-400/30',
    icon: <SunIcon className="w-8 h-8" data-id="element-14369" />,
    extraInfo: 'Солярный год: 2026'
  },
  compatibility: {
    title: 'Совместимость (Синастрия)',
    subtitle: 'Астрологический анализ отношений',
    gradient: 'from-slate-900 via-rose-900 to-slate-900',
    accentColor: 'rose',
    badgeClass: 'bg-rose-500/30 text-rose-200 border-rose-400/30',
    icon: <HeartIcon className="w-8 h-8" data-id="element-14370" />,
    person2: {
      name: 'Мария Иванова',
      date: '23 марта 1994',
      time: '09:15',
      place: 'Санкт-Петербург'
    }
  },
  combo: {
    title: 'Комбо: Натал + Соляр',
    subtitle: 'Полный анализ личности + прогноз на год',
    gradient: 'from-slate-900 via-purple-900 to-slate-900',
    accentColor: 'purple',
    badgeClass: 'bg-purple-500/30 text-purple-200 border-purple-400/30',
    icon: <LayersIcon className="w-8 h-8" data-id="element-14371" />,
    extraInfo: 'Солярный год: 2026'
  },
  forecast: {
    title: 'Прогноз (Транзиты)',
    subtitle: 'Анализ текущих и предстоящих транзитов',
    gradient: 'from-slate-900 via-teal-900 to-slate-900',
    accentColor: 'teal',
    badgeClass: 'bg-teal-500/30 text-teal-200 border-teal-400/30',
    icon: <ActivityIcon className="w-8 h-8" data-id="element-14372" />,
    extraInfo: 'Период: Март 2026 — Сентябрь 2026'
  }
};
const REPORT_PAGE_COUNT: Record<ServiceType, number> = {
  natal: 35,
  solar: 38,
  compatibility: 32,
  combo: 62,
  forecast: 30
};
const TOC_BY_SERVICE: Record<ServiceType, {
  num: string;
  title: string;
}[]> = {
  natal: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общий психологический портрет'
  }, {
    num: '03',
    title: 'Солнце — Ядро личности'
  }, {
    num: '04',
    title: 'Луна — Внутренний мир'
  }, {
    num: '05',
    title: 'Асцендент — Социальная маска'
  }, {
    num: '06',
    title: 'Меркурий — Мышление'
  }, {
    num: '07',
    title: 'Венера — Любовь и ценности'
  }, {
    num: '08',
    title: 'Марс — Энергия и действие'
  }, {
    num: '09',
    title: 'Планеты в домах'
  }, {
    num: '10',
    title: 'Аспекты между планетами'
  }, {
    num: '11',
    title: 'Карьера и финансы'
  }, {
    num: '12',
    title: 'Любовь и отношения'
  }, {
    num: '13',
    title: 'Кармические задачи'
  }, {
    num: '14',
    title: 'Таланты и предназначение'
  }, {
    num: '15',
    title: 'Итоговый анализ'
  }],
  solar: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая тема года'
  }, {
    num: '03',
    title: 'Асцендент соляра'
  }, {
    num: '04',
    title: 'Положение Солнца в соляре'
  }, {
    num: '05',
    title: 'Луна в соляре'
  }, {
    num: '06',
    title: 'Меркурий в соляре'
  }, {
    num: '07',
    title: 'Венера в соляре'
  }, {
    num: '08',
    title: 'Марс в соляре'
  }, {
    num: '09',
    title: 'Юпитер, Сатурн и внешние планеты'
  }, {
    num: '10',
    title: 'Аспекты соляра'
  }, {
    num: '11',
    title: 'Карьера и финансы'
  }, {
    num: '12',
    title: 'Отношения и семья'
  }, {
    num: '13',
    title: 'Личностное развитие и здоровье'
  }, {
    num: '14',
    title: 'Важные периоды года'
  }, {
    num: '15',
    title: 'Рекомендации на год'
  }, {
    num: '16',
    title: 'Итог прогноза'
  }],
  compatibility: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая динамика отношений'
  }, {
    num: '03',
    title: 'Солнце и личность партнёров'
  }, {
    num: '04',
    title: 'Эмоциональная совместимость'
  }, {
    num: '05',
    title: 'Романтическая и сексуальная совместимость'
  }, {
    num: '06',
    title: 'Интеллектуальная совместимость'
  }, {
    num: '07',
    title: 'Кармические связи'
  }, {
    num: '08',
    title: 'Планеты партнёра в домах другого'
  }, {
    num: '09',
    title: 'Основные аспекты между картами'
  }, {
    num: '10',
    title: 'Сильные стороны отношений'
  }, {
    num: '11',
    title: 'Возможные конфликты'
  }, {
    num: '12',
    title: 'Потенциал долгосрочных отношений'
  }, {
    num: '13',
    title: 'Практические рекомендации'
  }, {
    num: '14',
    title: 'Итог анализа'
  }],
  combo: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая характеристика личности'
  }, {
    num: '03',
    title: 'Солнце — Ядро личности'
  }, {
    num: '04',
    title: 'Луна — Эмоциональная природа'
  }, {
    num: '05',
    title: 'Асцендент — Внешнее проявление'
  }, {
    num: '06',
    title: 'Планеты в знаках'
  }, {
    num: '07',
    title: 'Планеты в домах'
  }, {
    num: '08',
    title: 'Аспекты натальной карты'
  }, {
    num: '09',
    title: 'Сферы жизни (натал)'
  }, {
    num: '10',
    title: 'Введение в соляр'
  }, {
    num: '11',
    title: 'Общая энергия 2026 года'
  }, {
    num: '12',
    title: 'Асцендент соляра'
  }, {
    num: '13',
    title: 'Солнце в соляре'
  }, {
    num: '14',
    title: 'Луна в соляре'
  }, {
    num: '15',
    title: 'Планеты соляра в домах'
  }, {
    num: '16',
    title: 'Аспекты соляра'
  }, {
    num: '17',
    title: 'Важные периоды года'
  }, {
    num: '18',
    title: 'Влияние соляра на натал'
  }, {
    num: '19',
    title: 'Сферы жизни (год)'
  }, {
    num: '20',
    title: 'Рекомендации на год'
  }, {
    num: '21',
    title: 'Итоговый анализ'
  }],
  forecast: [{
    num: '01',
    title: 'Введение'
  }, {
    num: '02',
    title: 'Общая энергия периода'
  }, {
    num: '03',
    title: 'Медленные транзиты'
  }, {
    num: '04',
    title: 'Транзиты личных планет'
  }, {
    num: '05',
    title: 'Транзиты по домам'
  }, {
    num: '06',
    title: 'Аспекты транзитов'
  }, {
    num: '07',
    title: 'Ключевые периоды'
  }, {
    num: '08',
    title: 'Сферы жизни'
  }, {
    num: '09',
    title: 'Возможности периода'
  }, {
    num: '10',
    title: 'Потенциальные трудности'
  }, {
    num: '11',
    title: 'Рекомендации'
  }, {
    num: '12',
    title: 'Итог прогноза'
  }]
};
// ═══════════════════════════════════════════
// NATAL DATA
// ═══════════════════════════════════════════
const NATAL_ELEMENT_BALANCE = [{
  element: 'Огонь',
  percent: 20,
  color: 'bg-red-500',
  planets: 'Солнце, Меркурий'
}, {
  element: 'Земля',
  percent: 30,
  color: 'bg-emerald-500',
  planets: 'Юпитер, Уран, Нептун'
}, {
  element: 'Воздух',
  percent: 20,
  color: 'bg-blue-500',
  planets: 'Марс, Сатурн'
}, {
  element: 'Вода',
  percent: 30,
  color: 'bg-indigo-500',
  planets: 'Луна, Венера, Плутон'
}];
const NATAL_ASPECTS_GRID = [{
  planets: '☉ ☌ ☿',
  type: 'Соединение',
  nature: 'intense'
}, {
  planets: '☽ △ ♀',
  type: 'Трин',
  nature: 'harmonious'
}, {
  planets: '♀ ⚹ ♃',
  type: 'Секстиль',
  nature: 'harmonious'
}, {
  planets: '♂ □ ♃',
  type: 'Квадрат',
  nature: 'tense'
}, {
  planets: '♂ □ ♄',
  type: 'Квадрат',
  nature: 'tense'
}];
// ═══════════════════════════════════════════
// SOLAR DATA
// ═══════════════════════════════════════════
const SOLAR_MONTHLY_FORECAST = [{
  month: 'Янв',
  intensity: 60,
  theme: 'Планирование'
}, {
  month: 'Фев',
  intensity: 55,
  theme: 'Подготовка'
}, {
  month: 'Мар',
  intensity: 90,
  theme: 'Прорыв'
}, {
  month: 'Апр',
  intensity: 85,
  theme: 'Рост'
}, {
  month: 'Май',
  intensity: 80,
  theme: 'Реализация'
}, {
  month: 'Июн',
  intensity: 65,
  theme: 'Стабилизация'
}, {
  month: 'Июл',
  intensity: 50,
  theme: 'Отдых'
}, {
  month: 'Авг',
  intensity: 70,
  theme: 'Новый цикл'
}, {
  month: 'Сен',
  intensity: 75,
  theme: 'Активность'
}, {
  month: 'Окт',
  intensity: 60,
  theme: 'Завершение'
}, {
  month: 'Ноя',
  intensity: 55,
  theme: 'Рефлексия'
}, {
  month: 'Дек',
  intensity: 65,
  theme: 'Итоги'
}];
const SOLAR_KEY_DATES = [{
  date: '15 марта',
  event: 'Новолуние в 10 доме',
  type: 'positive'
}, {
  date: '28 апреля',
  event: 'Юпитер тригон Солнце',
  type: 'positive'
}, {
  date: '12 июня',
  event: 'Сатурн квадрат Луна',
  type: 'caution'
}, {
  date: '8 августа',
  event: 'День рождения — новый соляр',
  type: 'neutral'
}, {
  date: '22 октября',
  event: 'Затмение в 5 доме',
  type: 'caution'
}, {
  date: '5 декабря',
  event: 'Венера секстиль Юпитер',
  type: 'positive'
}];
// ═══════════════════════════════════════════
// COMPATIBILITY DATA
// ═══════════════════════════════════════════
const COMPATIBILITY_SCORES = [{
  area: 'Эмоциональная связь',
  percent: 85,
  color: 'bg-rose-500'
}, {
  area: 'Романтика и страсть',
  percent: 92,
  color: 'bg-pink-500'
}, {
  area: 'Коммуникация',
  percent: 78,
  color: 'bg-sky-500'
}, {
  area: 'Быт и финансы',
  percent: 70,
  color: 'bg-emerald-500'
}, {
  area: 'Долгосрочные перспективы',
  percent: 88,
  color: 'bg-purple-500'
}];
const COMPATIBILITY_ASPECTS = [{
  aspect: '☉ △ ☽',
  desc: 'Солнце — Луна',
  nature: 'harmonious'
}, {
  aspect: '♀ ☌ ♂',
  desc: 'Венера — Марс',
  nature: 'intense'
}, {
  aspect: '☽ ⚹ ☽',
  desc: 'Луна — Луна',
  nature: 'harmonious'
}, {
  aspect: '☿ □ ☿',
  desc: 'Меркурий — Меркурий',
  nature: 'tense'
}, {
  aspect: '♃ △ ♀',
  desc: 'Юпитер — Венера',
  nature: 'harmonious'
}];
// ═══════════════════════════════════════════
// FORECAST DATA
// ═══════════════════════════════════════════
const FORECAST_ACTIVE_TRANSITS = [{
  planet: 'Юпитер',
  position: 'в 10 доме',
  effect: 'Карьерный рост',
  strength: 90,
  color: 'bg-amber-500'
}, {
  planet: 'Сатурн',
  position: 'в 7 доме',
  effect: 'Уроки в отношениях',
  strength: 75,
  color: 'bg-slate-500'
}, {
  planet: 'Уран',
  position: 'в 1 доме',
  effect: 'Личная трансформация',
  strength: 85,
  color: 'bg-cyan-500'
}, {
  planet: 'Нептун',
  position: 'в 9 доме',
  effect: 'Духовный поиск',
  strength: 60,
  color: 'bg-indigo-500'
}, {
  planet: 'Плутон',
  position: 'в 8 доме',
  effect: 'Глубокие перемены',
  strength: 70,
  color: 'bg-purple-500'
}];
const FORECAST_TIMELINE = [{
  period: 'Март',
  events: ['Юпитер тригон Солнце', 'Новолуние в 10 доме'],
  intensity: 'high'
}, {
  period: 'Апрель',
  events: ['Меркурий ретро', 'Венера в 7 доме'],
  intensity: 'medium'
}, {
  period: 'Май',
  events: ['Затмение в 11 доме', 'Марс секстиль Юпитер'],
  intensity: 'high'
}, {
  period: 'Июнь',
  events: ['Сатурн квадрат Луна'],
  intensity: 'low'
}];
export function PremiumReportPreview({
  serviceType
}: PremiumReportPreviewProps) {
  const config = SERVICE_CONFIG[serviceType];
  const toc = TOC_BY_SERVICE[serviceType];
  // Review state
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [reviewTags, setReviewTags] = useState<string[]>([]);
  const [reviewText, setReviewText] = useState('');
  // ═══════════════════════════════════════════
  // RENDER NATAL CONTENT
  // ═══════════════════════════════════════════
  const renderNatalContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle,
      color = 'indigo'
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      color?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-14373">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`} data-id="element-14374">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-14375">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-14376">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-14377">{subtitle}</p>}
        </div>
      </div>;
    const renderParagraphs = (text: string) => {
      return text.split('\n\n').map((paragraph, i) => <p key={i} className="text-sm sm:text-base text-slate-600 leading-relaxed mb-3 sm:mb-4 last:mb-0" data-id="element-14378">
          {paragraph}
        </p>);
    };
    return <>
        {/* 01. ВВЕДЕНИЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14379">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-14381" />} title={INTRO_CONTENT.title} subtitle="Что такое натальная карта и как работает астрология" data-id="element-14380" />
          <div className="space-y-6" data-id="element-14382">
            <div data-id="element-14383">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14384">
                Что такое натальная карта?
              </h3>
              {renderParagraphs(INTRO_CONTENT.whatIsNatalChart)}
            </div>
            <div data-id="element-14385">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14386">
                Как это работает?
              </h3>
              {renderParagraphs(INTRO_CONTENT.howItWorks)}
            </div>
            <div className="bg-indigo-50/50 p-5 rounded-xl border border-indigo-100" data-id="element-14387">
              <h3 className="font-bold text-indigo-800 mb-2" data-id="element-14388">
                Как читать этот отчёт
              </h3>
              {renderParagraphs(INTRO_CONTENT.howToRead)}
            </div>
          </div>
        </div>

        {/* 02. ОБЩИЙ ПСИХОЛОГИЧЕСКИЙ ПОРТРЕТ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-14389">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-14391" />} title="Общий психологический портрет" subtitle="Фундамент вашей личности" data-id="element-14390" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-8" data-id="element-14392">
            <Card padding="md" className="bg-white border-amber-200 shadow-sm" data-id="element-14393">
              <div className="flex items-center gap-2 mb-3" data-id="element-14394">
                <SunIcon className="w-5 h-5 text-amber-500" data-id="element-14395" />
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-14396">
                  Солнце (Эго)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-14397">
                <span className="text-amber-500" data-id="element-14398">♌</span> Лев
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-14399">
                Обитель Солнца • Огненная стихия
              </p>
            </Card>

            <Card padding="md" className="bg-white border-indigo-200 shadow-sm" data-id="element-14400">
              <div className="flex items-center gap-2 mb-3" data-id="element-14401">
                <MoonIcon className="w-5 h-5 text-indigo-500" data-id="element-14402" />
                <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-14403">
                  Луна (Душа)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-14404">
                <span className="text-indigo-500" data-id="element-14405">♏</span> Скорпион
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-14406">
                Падение Луны • Водная стихия
              </p>
            </Card>

            <Card padding="md" className="bg-white border-purple-200 shadow-sm" data-id="element-14407">
              <div className="flex items-center gap-2 mb-3" data-id="element-14408">
                <StarIcon className="w-5 h-5 text-purple-500" data-id="element-14409" />
                <span className="text-xs font-bold text-purple-600 uppercase tracking-wider" data-id="element-14410">
                  Асцендент (Маска)
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-id="element-14411">
                <span className="text-purple-500" data-id="element-14412">♐</span> Стрелец
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-14413">
                Управитель: Юпитер • Огненная стихия
              </p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8" data-id="element-14414">
            <div data-id="element-14415">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-14416">
                Баланс стихий
              </h3>
              <div className="space-y-4" data-id="element-14417">
                {NATAL_ELEMENT_BALANCE.map((item, i) => <div key={i} data-id="element-14418">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-14419">
                      <span className="font-medium text-slate-700" data-id="element-14420">
                        {item.element} ({item.percent}%)
                      </span>
                      <span className="text-slate-400 text-xs" data-id="element-14421">
                        {item.planets}
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-14422">
                      <motion.div className={`${item.color} h-2.5 rounded-full`} initial={{
                    width: 0
                  }} animate={{
                    width: `${item.percent}%`
                  }} transition={{
                    duration: 1,
                    delay: i * 0.15,
                    ease: 'easeOut'
                  }} data-id="element-14423" />
                    </div>
                  </div>)}
              </div>
            </div>

            <div data-id="element-14424">
              <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-14425">
                Энергетический профиль
              </h3>
              <div className="space-y-4" data-id="element-14426">
                {ENERGY_RADAR_DATA.axes.map((item, i) => <div key={i} data-id="element-14427">
                    <div className="flex justify-between text-sm mb-1.5" data-id="element-14428">
                      <span className="font-medium text-slate-700" data-id="element-14429">
                        {item.label}
                      </span>
                      <span className="font-bold text-indigo-600" data-id="element-14430">
                        {item.value}/100
                      </span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden" data-id="element-14431">
                      <motion.div className="bg-indigo-500 h-2.5 rounded-full" initial={{
                    width: 0
                  }} animate={{
                    width: `${item.value}%`
                  }} transition={{
                    duration: 1,
                    delay: i * 0.1,
                    ease: 'easeOut'
                  }} data-id="element-14432" />
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-4" data-id="element-14433">
            Положения планет
          </h3>
          <div className="-mx-4 sm:mx-0 overflow-x-auto" data-id="element-14434">
            <table className="w-full text-sm text-left border-collapse min-w-[500px]" data-id="element-14435">
              <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-14436">
                <tr data-id="element-14437">
                  <th className="px-4 py-3 rounded-tl-lg" data-id="element-14438">Планета</th>
                  <th className="px-4 py-3" data-id="element-14439">Знак</th>
                  <th className="px-4 py-3" data-id="element-14440">Градус</th>
                  <th className="px-4 py-3" data-id="element-14441">Дом</th>
                  <th className="px-4 py-3 rounded-tr-lg" data-id="element-14442">Достоинство</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white" data-id="element-14443">
                {PLANET_POSITIONS_TABLE.map((planet, i) => <tr key={i} className="hover:bg-slate-50/50 transition-colors" data-id="element-14444">
                    <td className="px-4 py-3 font-medium text-slate-900 flex items-center gap-2" data-id="element-14445">
                      <span className="text-lg text-indigo-500 w-6 text-center" data-id="element-14446">
                        {planet.symbol}
                      </span>
                      {planet.planet}
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-14447">{planet.sign}</td>
                    <td className="px-4 py-3 text-slate-500 font-mono text-xs" data-id="element-14448">
                      {planet.degree}
                    </td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-14449">{planet.house}</td>
                    <td className="px-4 py-3 text-slate-500" data-id="element-14450">
                      {planet.dignity}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        {/* 03. СОЛНЦЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14451">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-14453" />} title="Солнце во Льве" subtitle="Ядро личности, эго и жизненная сила" color="amber" data-id="element-14452" />
          <div className="space-y-6" data-id="element-14454">
            <div data-id="element-14455">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14456">Жизненная цель</h3>
              {renderParagraphs(EXTENDED_SUN.lifePurpose)}
            </div>
            <div data-id="element-14457">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14458">
                В повседневной жизни
              </h3>
              {renderParagraphs(EXTENDED_SUN.dailyLife)}
            </div>
            <div data-id="element-14459">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14460">В отношениях</h3>
              {renderParagraphs(EXTENDED_SUN.inRelationships)}
            </div>
          </div>
        </div>

        {/* 04. ЛУНА */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-14461">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-14463" />} title="Луна в Скорпионе" subtitle="Внутренний мир, эмоции и подсознание" color="indigo" data-id="element-14462" />
          <div className="space-y-6" data-id="element-14464">
            <div data-id="element-14465">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14466">
                Эмоциональные паттерны
              </h3>
              {renderParagraphs(EXTENDED_MOON.emotionalPatterns)}
            </div>
            <div data-id="element-14467">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14468">
                Отношения с матерью
              </h3>
              {renderParagraphs(EXTENDED_MOON.motherRelation)}
            </div>
            <div className="bg-indigo-50/50 p-5 rounded-xl border border-indigo-100" data-id="element-14469">
              <h3 className="font-bold text-indigo-800 mb-2" data-id="element-14470">
                Скрытые потребности
              </h3>
              {renderParagraphs(EXTENDED_MOON.hiddenNeeds)}
            </div>
          </div>
        </div>

        {/* 05. АСЦЕНДЕНТ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14471">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-14473" />} title="Асцендент в Стрельце" subtitle="Социальная маска и первое впечатление" color="purple" data-id="element-14472" />
          <div className="space-y-4" data-id="element-14474">
            <p className="text-slate-600 leading-relaxed" data-id="element-14475">
              Асцендент в Стрельце формирует ваш внешний образ — оптимистичный,
              открытый и любознательный. Люди воспринимают вас как человека с
              широким кругозором и философским взглядом на жизнь. Вы производите
              впечатление человека, который знает, куда идёт, и приглашает
              других присоединиться к путешествию.
            </p>
            <p className="text-slate-600 leading-relaxed" data-id="element-14476">
              Ваше тело, скорее всего, крупное или высокое, с широкими жестами и
              открытой позой. Вы излучаете энтузиазм и уверенность. Однако за
              этой жизнерадостной маской скрывается глубокий и интенсивный
              внутренний мир (Луна в Скорпионе), который вы открываете далеко не
              всем.
            </p>
          </div>
        </div>

        {/* 06. МЕРКУРИЙ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-sky-50/30 to-white" data-id="element-14477">
          <SectionHeader icon={<MessageCircleIcon className="w-5 h-5" data-id="element-14479" />} title="Меркурий во Льве" subtitle="Мышление, речь и коммуникация" color="sky" data-id="element-14478" />
          <div className="space-y-6" data-id="element-14480">
            <div data-id="element-14481">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14482">Стиль обучения</h3>
              {renderParagraphs(EXTENDED_MERCURY.learningStyle)}
            </div>
            <div data-id="element-14483">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14484">Стиль общения</h3>
              {renderParagraphs(EXTENDED_MERCURY.communicationStyle)}
            </div>
            <div data-id="element-14485">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14486">
                Принятие решений
              </h3>
              {renderParagraphs(EXTENDED_MERCURY.decisionMaking)}
            </div>
          </div>
        </div>

        {/* 07. ВЕНЕРА */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14487">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-14489" />} title="Венера в Раке" subtitle="Любовь, ценности и эстетика" color="rose" data-id="element-14488" />
          <div className="space-y-6" data-id="element-14490">
            <div data-id="element-14491">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14492">Язык любви</h3>
              {renderParagraphs(EXTENDED_VENUS.loveLanguage)}
            </div>
            <div data-id="element-14493">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14494">Эстетика и вкус</h3>
              {renderParagraphs(EXTENDED_VENUS.aesthetics)}
            </div>
            <div data-id="element-14495">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14496">
                Финансовые привычки
              </h3>
              {renderParagraphs(EXTENDED_VENUS.financialHabits)}
            </div>
          </div>
        </div>

        {/* 08. МАРС */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-14497">
          <SectionHeader icon={<ZapIcon className="w-5 h-5" data-id="element-14499" />} title="Марс в Близнецах" subtitle="Энергия, действие и конфликт" color="amber" data-id="element-14498" />
          <div className="space-y-6" data-id="element-14500">
            <div data-id="element-14501">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14502">Стиль конфликта</h3>
              {renderParagraphs(EXTENDED_MARS.conflictStyle)}
            </div>
            <div data-id="element-14503">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14504">
                Амбиции и действия
              </h3>
              {renderParagraphs(EXTENDED_MARS.ambitionStyle)}
            </div>
            <div data-id="element-14505">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14506">
                Физическая энергия
              </h3>
              {renderParagraphs(EXTENDED_MARS.physicalEnergy)}
            </div>
          </div>
        </div>

        {/* 09. ПЛАНЕТЫ В ДОМАХ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14507">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-14509" />} title="Планеты в домах" subtitle="Сферы жизни и где проявляется ваша энергия" color="indigo" data-id="element-14508" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-14510">
            {HOUSES_DETAILED.map((house, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className={`rounded-xl p-5 border ${house.planets.length > 0 ? 'bg-indigo-50/50 border-indigo-200 shadow-sm' : 'bg-slate-50 border-slate-200'}`} data-id="element-14511">
                <div className="flex items-center justify-between mb-3" data-id="element-14512">
                  <div className="flex items-center gap-2" data-id="element-14513">
                    <span className={`w-6 h-6 rounded-md flex items-center justify-center text-xs font-bold ${house.planets.length > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-200 text-slate-600'}`} data-id="element-14514">
                      {house.num}
                    </span>
                    <h4 className="font-bold text-slate-900 text-sm" data-id="element-14515">
                      {house.theme}
                    </h4>
                  </div>
                  <span className="text-xs font-medium text-slate-500" data-id="element-14516">
                    {house.sign}
                  </span>
                </div>
                {house.planets.length > 0 && <div className="mb-2 flex flex-wrap gap-1" data-id="element-14517">
                    {house.planets.map((p, idx) => <Badge key={idx} variant="pro" className="text-[10px] px-1.5 py-0" data-id="element-14518">
                        {p}
                      </Badge>)}
                  </div>}
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-14519">
                  {house.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* 10. АСПЕКТЫ МЕЖДУ ПЛАНЕТАМИ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-14520">
          <SectionHeader icon={<ActivityIcon className="w-5 h-5" data-id="element-14522" />} title="Аспекты между планетами" subtitle="Внутренние диалоги и конфликты" color="indigo" data-id="element-14521" />
          <div className="space-y-4" data-id="element-14523">
            {ASPECTS_DETAILED.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            const isIntense = aspect.nature === 'intense';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.08
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : isIntense ? 'bg-purple-50/50 border-purple-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-14524">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-14525">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-14526">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : isIntense ? 'text-purple-600' : 'text-rose-600'}`} data-id="element-14527">
                        {aspect.symbol}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-14528">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : isIntense ? 'pro' : 'warning'} className="text-xs" data-id="element-14529">
                      {isHarmonious ? 'Гармоничный' : isIntense ? 'Интенсивный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-14530">
                    {aspect.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* 11. КАРЬЕРА И ФИНАНСЫ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14531">
          <SectionHeader icon={<BriefcaseIcon className="w-5 h-5" data-id="element-14533" />} title="Карьера и финансы" subtitle="Профессиональный потенциал и заработок" color="emerald" data-id="element-14532" />
          <div className="space-y-6" data-id="element-14534">
            <div data-id="element-14535">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14536">
                Профессиональный путь
              </h3>
              {renderParagraphs(EXTENDED_JUPITER)}
            </div>
            <div data-id="element-14537">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14538">
                Финансовая стратегия
              </h3>
              {renderParagraphs(OUTER_PLANETS_EXTENDED.uranus)}
            </div>
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200" data-id="element-14539">
              <h3 className="font-bold text-emerald-800 mb-3" data-id="element-14540">
                Лучшие сферы для реализации
              </h3>
              <ul className="space-y-2" data-id="element-14541">
                {TALENTS_SECTION.bestAreas.split('\n').filter(line => line.trim().startsWith('•')).map((area, i) => <li key={i} className="flex items-start gap-2 text-sm text-emerald-700" data-id="element-14542">
                      <CheckIcon className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-14543" />
                      {area.replace('•', '').trim()}
                    </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* 12. ЛЮБОВЬ И ОТНОШЕНИЯ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-14544">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-14546" />} title="Любовь и отношения" subtitle="Совместимость и партнёрство" color="rose" data-id="element-14545" />
          <div className="space-y-6 mb-8" data-id="element-14547">
            <p className="text-slate-600 leading-relaxed" data-id="element-14548">
              Ваш подход к отношениям формируется Венерой в Раке (потребность в
              заботе и уюте), Марсом в Близнецах (потребность в интеллектуальном
              стимулировании) и 7 домом в Близнецах. Вы ищете партнёра, с
              которым можно и глубоко помолчать, и жарко поспорить.
            </p>
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-14549">
            Совместимость со знаками зодиака
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-id="element-14550">
            {ZODIAC_COMPATIBILITY.map((sign, i) => <div key={i} className={`p-3 rounded-xl border ${sign.best ? 'bg-rose-50 border-rose-200 shadow-sm' : 'bg-white border-slate-200'}`} data-id="element-14551">
                <div className="flex items-center justify-between mb-2" data-id="element-14552">
                  <div className="flex items-center gap-1.5" data-id="element-14553">
                    <span className={`text-lg ${sign.best ? 'text-rose-500' : 'text-slate-400'}`} data-id="element-14554">
                      {sign.symbol}
                    </span>
                    <span className="font-bold text-slate-800 text-sm" data-id="element-14555">
                      {sign.sign}
                    </span>
                  </div>
                  <span className={`text-xs font-bold ${sign.percent >= 80 ? 'text-emerald-600' : sign.percent >= 70 ? 'text-amber-600' : 'text-slate-500'}`} data-id="element-14556">
                    {sign.percent}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 mb-2 overflow-hidden" data-id="element-14557">
                  <div className={`h-1.5 rounded-full ${sign.percent >= 80 ? 'bg-emerald-500' : sign.percent >= 70 ? 'bg-amber-500' : 'bg-slate-400'}`} style={{
                width: `${sign.percent}%`
              }} data-id="element-14558" />
                </div>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-14559">
                  {sign.note}
                </p>
              </div>)}
          </div>
        </div>

        {/* 13. КАРМИЧЕСКИЕ ЗАДАЧИ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14560">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-14562" />} title="Кармические задачи" subtitle="Предназначение и уроки души" color="purple" data-id="element-14561" />
          <div className="space-y-6" data-id="element-14563">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-14564">
              <div className="bg-purple-50 rounded-xl p-5 border border-purple-200" data-id="element-14565">
                <h3 className="font-bold text-purple-800 mb-2 flex items-center gap-2" data-id="element-14566">
                  <TrendingUpIcon className="w-4 h-4" data-id="element-14567" /> Вектор развития
                  (Северный узел)
                </h3>
                {renderParagraphs(KARMIC_SECTION.northNode)}
              </div>
              <div className="bg-slate-50 rounded-xl p-5 border border-slate-200" data-id="element-14568">
                <h3 className="font-bold text-slate-700 mb-2 flex items-center gap-2" data-id="element-14569">
                  <AlertTriangleIcon className="w-4 h-4" data-id="element-14570" /> Прошлый опыт (Южный
                  узел)
                </h3>
                {renderParagraphs(KARMIC_SECTION.southNode)}
              </div>
            </div>

            <div data-id="element-14571">
              <h3 className="font-bold text-slate-800 mb-2" data-id="element-14572">
                Главный урок Сатурна
              </h3>
              {renderParagraphs(KARMIC_SECTION.saturnLesson)}
            </div>

            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg" data-id="element-14573">
              <h3 className="font-bold text-white mb-2 text-lg" data-id="element-14574">
                Жизненная миссия
              </h3>
              <p className="text-indigo-100 leading-relaxed" data-id="element-14575">
                {KARMIC_SECTION.lifeMission}
              </p>
            </div>

            <div className="mt-8" data-id="element-14576">
              <h3 className="font-bold text-slate-800 mb-4" data-id="element-14577">Главные вызовы</h3>
              <div className="space-y-3" data-id="element-14578">
                {CHALLENGES_SECTION.mainChallenges.split('\n\n').filter(p => !p.startsWith('Каждая')).map((challenge, i) => <div key={i} className="flex items-start gap-3 bg-white p-4 rounded-xl border border-rose-100" data-id="element-14579">
                      <div className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" data-id="element-14580">
                        {i + 1}
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14581">
                        {challenge}
                      </p>
                    </div>)}
              </div>
            </div>
          </div>
        </div>

        {/* 14. ТАЛАНТЫ И ПРЕДНАЗНАЧЕНИЕ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-14582">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-14584" />} title="Таланты и здоровье" subtitle="Ваши сильные стороны и физический потенциал" color="amber" data-id="element-14583" />

          <div className="space-y-8" data-id="element-14585">
            <div data-id="element-14586">
              <h3 className="font-bold text-slate-800 mb-4" data-id="element-14587">
                Природные способности
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-14588">
                {TALENTS_SECTION.naturalAbilities.split('\n\n').filter(p => !p.startsWith('На основе')).map((talent, i) => <div key={i} className="bg-white rounded-xl p-4 border border-amber-200 shadow-sm" data-id="element-14589">
                      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-14590">
                        {talent}
                      </p>
                    </div>)}
              </div>
            </div>

            <div data-id="element-14591">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2" data-id="element-14592">
                <ActivityIcon className="w-5 h-5 text-emerald-500" data-id="element-14593" />{' '}
                Астрологическое здоровье
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-14594">
                {HEALTH_SECTION.overview}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-14595">
                <Card padding="md" className="bg-emerald-50/50 border-emerald-100" data-id="element-14596">
                  <h4 className="font-bold text-emerald-800 mb-2 text-sm" data-id="element-14597">
                    Витальность (Солнце)
                  </h4>
                  <p className="text-xs text-slate-600" data-id="element-14598">
                    {HEALTH_SECTION.sunHealth}
                  </p>
                </Card>
                <Card padding="md" className="bg-indigo-50/50 border-indigo-100" data-id="element-14599">
                  <h4 className="font-bold text-indigo-800 mb-2 text-sm" data-id="element-14600">
                    Психосоматика (Луна)
                  </h4>
                  <p className="text-xs text-slate-600" data-id="element-14601">
                    {HEALTH_SECTION.moonHealth}
                  </p>
                </Card>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-14602">
                <h4 className="font-bold text-slate-800 mb-3 text-sm" data-id="element-14603">
                  Рекомендации по здоровью
                </h4>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-14604">
                  {HEALTH_SECTION.recommendations.split('\n').map((rec, i) => <li key={i} className="flex items-start gap-2 text-xs text-slate-600" data-id="element-14605">
                      <CheckIcon className="w-3.5 h-3.5 mt-0.5 text-emerald-500 flex-shrink-0" data-id="element-14606" />
                      {rec.replace('•', '').trim()}
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* 15. ИТОГОВЫЙ АНАЛИЗ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14607">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-14609" />} title="Итоговый анализ" subtitle="Синтез натальной карты" color="indigo" data-id="element-14608" />

          <div className="space-y-6 mb-8" data-id="element-14610">
            {renderParagraphs(FINAL_ANALYSIS.summary)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-14611">
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-200" data-id="element-14612">
              <h3 className="font-bold text-indigo-800 mb-3" data-id="element-14613">
                Ключевые качества
              </h3>
              <ul className="space-y-2" data-id="element-14614">
                {FINAL_ANALYSIS.keyQualities.map((q, i) => <li key={i} className="flex items-start gap-2 text-sm text-indigo-900" data-id="element-14615">
                    <StarIcon className="w-4 h-4 mt-0.5 text-indigo-500 flex-shrink-0" data-id="element-14616" />
                    {q}
                  </li>)}
              </ul>
            </div>
            <div className="bg-amber-50 rounded-xl p-5 border border-amber-200" data-id="element-14617">
              <h3 className="font-bold text-amber-800 mb-3" data-id="element-14618">
                Главные темы жизни
              </h3>
              <ul className="space-y-2" data-id="element-14619">
                {FINAL_ANALYSIS.lifeThemes.map((t, i) => <li key={i} className="flex items-start gap-2 text-sm text-amber-900" data-id="element-14620">
                    <CompassIcon className="w-4 h-4 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-14621" />
                    {t}
                  </li>)}
              </ul>
            </div>
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-14622">
            Ваши персональные аффирмации
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8" data-id="element-14623">
            {PERSONAL_AFFIRMATIONS.map((aff, i) => <div key={i} className={`p-4 rounded-xl border border-slate-200 bg-gradient-to-br ${aff.gradient}`} data-id="element-14624">
                <p className="font-medium text-slate-800 text-sm mb-2" data-id="element-14625">
                  «{aff.text}»
                </p>
                <p className="text-[10px] text-slate-500 uppercase tracking-wider" data-id="element-14626">
                  {aff.source}
                </p>
              </div>)}
          </div>

          <h3 className="font-bold text-slate-800 mb-4" data-id="element-14627">
            Знаменитости с похожими показателями
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-14628">
            {CELEBRITY_MATCHES.map((celeb, i) => <div key={i} className="flex items-center gap-4 p-3 rounded-xl border border-slate-100 bg-slate-50" data-id="element-14629">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${celeb.color}`} data-id="element-14630">
                  {celeb.initials}
                </div>
                <div data-id="element-14631">
                  <div className="font-bold text-slate-900 text-sm" data-id="element-14632">
                    {celeb.name}
                  </div>
                  <div className="text-xs text-indigo-600 font-medium mb-0.5" data-id="element-14633">
                    {celeb.sharedPlacement}
                  </div>
                  <div className="text-[10px] text-slate-500" data-id="element-14634">{celeb.note}</div>
                </div>
              </div>)}
          </div>

          <div className="bg-gradient-to-br from-amber-100 via-yellow-50 to-amber-100 rounded-2xl p-6 sm:p-8 text-center shadow-sm border border-amber-200/60" data-id="element-14635">
            <SparklesIcon className="w-8 h-8 text-amber-500 mx-auto mb-4" data-id="element-14636" />
            <p className="text-base sm:text-lg font-semibold leading-relaxed text-slate-800 italic max-w-2xl mx-auto" data-id="element-14637">
              {FINAL_ANALYSIS.finalAdvice}
            </p>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER SOLAR CONTENT
  // ═══════════════════════════════════════════
  const renderSolarContent = () => {
    const planetIcons: Record<string, React.ReactNode> = {
      Меркурий: <MessageCircleIcon className="w-5 h-5" data-id="element-14638" />,
      Венера: <HeartIcon className="w-5 h-5" data-id="element-14639" />,
      Марс: <ZapIcon className="w-5 h-5" data-id="element-14640" />,
      Юпитер: <TrendingUpIcon className="w-5 h-5" data-id="element-14641" />,
      Сатурн: <ShieldIcon className="w-5 h-5" data-id="element-14642" />,
      Уран: <SparklesIcon className="w-5 h-5" data-id="element-14643" />,
      Нептун: <CompassIcon className="w-5 h-5" data-id="element-14644" />,
      Плутон: <ActivityIcon className="w-5 h-5" data-id="element-14645" />
    };
    const areaIcons: Record<string, React.ReactNode> = {
      briefcase: <BriefcaseIcon className="w-5 h-5" data-id="element-14646" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-14647" />,
      home: <StarIcon className="w-5 h-5" data-id="element-14648" />,
      'trending-up': <TrendingUpIcon className="w-5 h-5" data-id="element-14649" />,
      activity: <ActivityIcon className="w-5 h-5" data-id="element-14650" />,
      users: <UsersIcon className="w-5 h-5" data-id="element-14651" />
    };
    const areaColorMap: Record<string, {
      bg: string;
      border: string;
      text: string;
      iconBg: string;
    }> = {
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        text: 'text-emerald-700',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        text: 'text-rose-700',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        text: 'text-amber-700',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-700',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        text: 'text-teal-700',
        iconBg: 'bg-teal-100 text-teal-600'
      },
      sky: {
        bg: 'bg-sky-50',
        border: 'border-sky-200',
        text: 'text-sky-700',
        iconBg: 'bg-sky-100 text-sky-600'
      }
    };
    // Helper: render a section header
    const SectionHeader = ({
      icon,
      title,
      subtitle
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-14652">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-14653">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-14654">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-14655">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-14656">{subtitle}</p>}
        </div>
      </div>;
    // Helper: render strengths/cautions cards
    const StrengthsCautions = ({
      strengths,
      cautions
    }: {
      strengths: string[];
      cautions: string[];
    }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-14657">
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-14658">
          <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-14659">
            <ShieldIcon className="w-4 h-4" data-id="element-14660" /> Сильные стороны
          </h4>
          <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-14661">
            {strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-14662">
                <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-14663" />
                {s}
              </li>)}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-14664">
          <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-14665">
            <AlertTriangleIcon className="w-4 h-4" data-id="element-14666" /> Зоны внимания
          </h4>
          <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-14667">
            {cautions.map((c, i) => <li key={i} className="flex items-start gap-2" data-id="element-14668">
                <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-14669" />
                {c}
              </li>)}
          </ul>
        </div>
      </div>;
    // Individual planet sections (Mercury, Venus, Mars shown separately)
    const individualPlanets = SOLAR_PLANETS_IN_HOUSES.slice(0, 3);
    // Grouped planets (Jupiter, Saturn, outer)
    const groupedPlanets = SOLAR_PLANETS_IN_HOUSES.slice(3);
    return <>
        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14670">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-14672" />} title={SOLAR_INTRO.title} subtitle="Что такое соляр и как работает прогноз" data-id="element-14671" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14673">
            <p data-id="element-14674">{SOLAR_INTRO.whatIsSolar}</p>
            <p data-id="element-14675">{SOLAR_INTRO.howItAffects}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ТЕМА ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/50 to-white" data-id="element-14676">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-14678" />} title="Общая тема года" subtitle="Ключевые энергии 2026 года" data-id="element-14677" />

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 mb-6" data-id="element-14679">
            <Card padding="md" className="bg-white border-amber-200" data-id="element-14680">
              <div className="flex items-center gap-2 mb-3" data-id="element-14681">
                <TargetIcon className="w-5 h-5 text-amber-500" data-id="element-14682" />
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-14683">
                  Тема года
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-14684">
                {SOLAR_YEAR_THEME.mainEnergy}
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-14685">
                Солнце и Сатурн в 10 доме
              </p>
            </Card>
            <Card padding="md" className="bg-white border-orange-200" data-id="element-14686">
              <div className="flex items-center gap-2 mb-3" data-id="element-14687">
                <BriefcaseIcon className="w-5 h-5 text-orange-500" data-id="element-14688" />
                <span className="text-xs font-bold text-orange-600 uppercase tracking-wider" data-id="element-14689">
                  Ключевая сфера
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-14690">
                Карьера и статус
              </div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-14691">10 дом активирован</p>
            </Card>
            <Card padding="md" className="bg-white border-emerald-200" data-id="element-14692">
              <div className="flex items-center gap-2 mb-3" data-id="element-14693">
                <TrendingUpIcon className="w-5 h-5 text-emerald-500" data-id="element-14694" />
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider" data-id="element-14695">
                  Лучший период
                </span>
              </div>
              <div className="text-lg font-bold text-slate-900" data-id="element-14696">Март — Май</div>
              <p className="text-xs text-slate-500 mt-2" data-id="element-14697">
                Максимум возможностей
              </p>
            </Card>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14698">
            <p data-id="element-14699">{SOLAR_YEAR_THEME.description}</p>
            <p data-id="element-14700">
              <strong className="text-slate-800" data-id="element-14701">Основные изменения:</strong>{' '}
              {SOLAR_YEAR_THEME.changes}
            </p>
            <p data-id="element-14702">
              <strong className="text-slate-800" data-id="element-14703">
                Психологическая атмосфера:
              </strong>{' '}
              {SOLAR_YEAR_THEME.atmosphere}
            </p>
            <p data-id="element-14704">
              <strong className="text-slate-800" data-id="element-14705">Точки роста:</strong>{' '}
              {SOLAR_YEAR_THEME.growthPoints}
            </p>
          </div>

          <div className="mt-6 bg-amber-100/50 rounded-xl p-4 border border-amber-200" data-id="element-14706">
            <h4 className="font-bold text-amber-800 mb-3" data-id="element-14707">
              Главные задачи года
            </h4>
            <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-14708">
              {SOLAR_YEAR_THEME.tasks.map((task, i) => <li key={i} className="flex items-start gap-2" data-id="element-14709">
                  <TargetIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-14710" />
                  {task}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 03. АСЦЕНДЕНТ СОЛЯРА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14711">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-14713" />} title={SOLAR_ASCENDANT.title} subtitle={`Восходящий знак: ${SOLAR_ASCENDANT.symbol} ${SOLAR_ASCENDANT.sign}`} data-id="element-14712" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14714">
            <p data-id="element-14715">{SOLAR_ASCENDANT.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 my-6" data-id="element-14716">
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-14717">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-14718">
                  Проявление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-14719">
                  {SOLAR_ASCENDANT.manifestation}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-amber-200" data-id="element-14720">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-14721">
                  Стратегия
                </h4>
                <p className="text-sm text-slate-600" data-id="element-14722">
                  {SOLAR_ASCENDANT.strategy}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-emerald-200" data-id="element-14723">
                <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-14724">
                  Восприятие
                </h4>
                <p className="text-sm text-slate-600" data-id="element-14725">
                  {SOLAR_ASCENDANT.perception}
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* ══════════ 04. СОЛНЦЕ В СОЛЯРЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-14726">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-14728" />} title={SOLAR_SUN_POSITION.title} subtitle={`${SOLAR_SUN_POSITION.sign} • ${SOLAR_SUN_POSITION.house}-й дом`} data-id="element-14727" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14729">
            <p data-id="element-14730">{SOLAR_SUN_POSITION.description}</p>
            <p data-id="element-14731">
              <strong className="text-slate-800" data-id="element-14732">Ключевая сфера:</strong>{' '}
              {SOLAR_SUN_POSITION.keySphere}
            </p>
            <p data-id="element-14733">{SOLAR_SUN_POSITION.focus}</p>
          </div>

          <div className="mt-6 bg-white rounded-xl p-4 border border-amber-200" data-id="element-14734">
            <h4 className="font-bold text-amber-800 mb-3" data-id="element-14735">Возможные события</h4>
            <ul className="space-y-1.5 text-sm text-slate-700" data-id="element-14736">
              {SOLAR_SUN_POSITION.events.map((event, i) => <li key={i} className="flex items-start gap-2" data-id="element-14737">
                  <SunIcon className="w-3.5 h-3.5 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-14738" />
                  {event}
                </li>)}
            </ul>
          </div>

          <StrengthsCautions strengths={SOLAR_SUN_POSITION.strengths} cautions={SOLAR_SUN_POSITION.cautions} data-id="element-14739" />
        </div>

        {/* ══════════ 05. ЛУНА В СОЛЯРЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14740">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-14742" />} title={SOLAR_MOON.title} subtitle={`${SOLAR_MOON.sign} • ${SOLAR_MOON.house}-й дом`} data-id="element-14741" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14743">
            <p data-id="element-14744">{SOLAR_MOON.description}</p>
            <p data-id="element-14745">
              <strong className="text-slate-800" data-id="element-14746">Эмоциональный фон:</strong>{' '}
              {SOLAR_MOON.emotions}
            </p>
            <p data-id="element-14747">
              <strong className="text-slate-800" data-id="element-14748">
                Внутренние потребности:
              </strong>{' '}
              {SOLAR_MOON.needs}
            </p>
            <p data-id="element-14749">
              <strong className="text-slate-800" data-id="element-14750">Отношения с близкими:</strong>{' '}
              {SOLAR_MOON.relations}
            </p>
          </div>

          <StrengthsCautions strengths={SOLAR_MOON.strengths} cautions={SOLAR_MOON.cautions} data-id="element-14751" />
        </div>

        {/* ══════════ 06-08. INDIVIDUAL PLANETS (Mercury, Venus, Mars) ══════════ */}
        {individualPlanets.map((planet, idx) => <div key={idx} className={`p-4 sm:p-6 md:p-8 border-b border-slate-100 ${idx % 2 === 0 ? 'bg-gradient-to-br from-slate-50/50 to-white' : ''}`} data-id="element-14752">
            <SectionHeader icon={planetIcons[planet.planet] || <StarIcon className="w-5 h-5" data-id="element-14754" />} title={planet.title} subtitle={`${planet.symbol} ${planet.sign} • ${planet.house}-й дом`} data-id="element-14753" />
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-14755">
              {planet.description}
            </p>
            <div className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-14756">
              <h4 className="font-bold text-slate-800 mb-3" data-id="element-14757">
                Возможные события
              </h4>
              <ul className="space-y-1.5 text-sm text-slate-700" data-id="element-14758">
                {planet.events.map((event, i) => <li key={i} className="flex items-start gap-2" data-id="element-14759">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 text-amber-500 flex-shrink-0" data-id="element-14760" />
                    {event}
                  </li>)}
              </ul>
            </div>
          </div>)}

        {/* ══════════ 09. ЮПИТЕР, САТУРН И ВНЕШНИЕ ПЛАНЕТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-14761">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-14763" />} title="Юпитер, Сатурн и внешние планеты" subtitle="Долгосрочные тенденции года" data-id="element-14762" />

          <div className="space-y-6" data-id="element-14764">
            {groupedPlanets.map((planet, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-14765">
                <div className="flex items-center gap-3 mb-3" data-id="element-14766">
                  <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-lg" data-id="element-14767">
                    {planet.symbol}
                  </div>
                  <div data-id="element-14768">
                    <h4 className="font-bold text-slate-900" data-id="element-14769">{planet.title}</h4>
                    <p className="text-xs text-slate-500" data-id="element-14770">
                      {planet.sign} • {planet.house}-й дом
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-14771">
                  {planet.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-14772">
                  {planet.events.map((event, i) => <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full border border-indigo-100" data-id="element-14773">
                      {event}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 10. АСПЕКТЫ СОЛЯРА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14774">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-14776" />} title="Аспекты соляра" subtitle="Взаимодействие планетарных энергий года" color="amber" data-id="element-14775" />
          <div className="space-y-4" data-id="element-14777">
            {COMBO_SOLAR_ASPECTS.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.1
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-14778">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-14779">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-14780">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : 'text-rose-600'}`} data-id="element-14781">
                        {aspect.aspect}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-14782">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : 'warning'} className="text-xs" data-id="element-14783">
                      {isHarmonious ? 'Гармоничный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-14784">
                    {aspect.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 11-13. СФЕРЫ ЖИЗНИ ══════════ */}
        {SOLAR_LIFE_AREAS.map((area, idx) => {
        const colors = areaColorMap[area.color] || areaColorMap.emerald;
        // Group: 11 = career+relationships, 12 = family, 13 = growth+health+social
        // Show section headers for grouping
        const showCareerHeader = idx === 0;
        const showRelationsHeader = idx === 1;
        const showGrowthHeader = idx === 3;
        return <Fragment key={area.id} data-id="element-14785">
              {showCareerHeader && <div className="p-4 sm:p-6 md:p-8 pb-0 border-b-0" data-id="element-14786">
                  <SectionHeader icon={<BriefcaseIcon className="w-5 h-5" data-id="element-14788" />} title="Ключевые сферы жизни года" subtitle="Подробный разбор каждой области" data-id="element-14787" />
                </div>}
              <div className={`p-4 sm:p-6 md:p-8 pb-6 ${idx === SOLAR_LIFE_AREAS.length - 1 ? 'border-b border-slate-100' : ''}`} data-id="element-14789">
                <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-14790">
                  <div className="flex items-center gap-3 mb-3" data-id="element-14791">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-14792">
                      {areaIcons[area.icon] || <StarIcon className="w-4 h-4" data-id="element-14793" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-14794">{area.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-14795">
                    {area.analysis}
                  </p>
                </motion.div>
              </div>
            </Fragment>;
      })}

        {/* ══════════ 14. ВАЖНЫЕ ПЕРИОДЫ ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14796">
          <SectionHeader icon={<CalendarIcon className="w-5 h-5" data-id="element-14798" />} title="Важные периоды года" subtitle="Помесячный прогноз активности" data-id="element-14797" />

          {/* Monthly Forecast Chart */}
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-12 gap-1.5 sm:gap-2 mb-6" data-id="element-14799">
            {SOLAR_MONTHLY_FORECAST.map((month, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.05
          }} className="text-center" data-id="element-14800">
                <div className="text-[10px] text-slate-400 mb-1" data-id="element-14801">
                  {month.month}
                </div>
                <div className="relative h-20 bg-slate-100 rounded-full overflow-hidden" data-id="element-14802">
                  <motion.div className={`absolute bottom-0 w-full rounded-full ${month.intensity >= 80 ? 'bg-amber-500' : month.intensity >= 60 ? 'bg-amber-400' : 'bg-amber-300'}`} initial={{
                height: 0
              }} animate={{
                height: `${month.intensity}%`
              }} transition={{
                duration: 0.8,
                delay: i * 0.05
              }} data-id="element-14803" />
                </div>
                <div className="text-[9px] text-slate-400 mt-1 truncate" data-id="element-14804">
                  {month.theme}
                </div>
              </motion.div>)}
          </div>
          <div className="flex justify-center gap-4 mb-8 text-xs text-slate-500" data-id="element-14805">
            <span className="flex items-center gap-1" data-id="element-14806">
              <span className="w-3 h-3 rounded-full bg-amber-500" data-id="element-14807"></span>{' '}
              Высокая активность
            </span>
            <span className="flex items-center gap-1" data-id="element-14808">
              <span className="w-3 h-3 rounded-full bg-amber-300" data-id="element-14809"></span>{' '}
              Умеренная
            </span>
          </div>

          {/* Key Dates Grid */}
          <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-14810">
            Ключевые даты года
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-14811">
            {SOLAR_KEY_DATES.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.1
          }} className={`rounded-xl p-3 border ${item.type === 'positive' ? 'bg-emerald-50 border-emerald-200' : item.type === 'caution' ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-14812">
                <div className={`text-sm font-bold ${item.type === 'positive' ? 'text-emerald-700' : item.type === 'caution' ? 'text-amber-700' : 'text-slate-700'}`} data-id="element-14813">
                  {item.date}
                </div>
                <div className="text-xs text-slate-500 mt-1" data-id="element-14814">{item.event}</div>
              </motion.div>)}
          </div>

          {/* Sample Month Deep Dive */}
          <div className="mt-8" data-id="element-14815">
            <div className="flex items-center gap-3 mb-4" data-id="element-14816">
              <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center" data-id="element-14817">
                <CalendarIcon className="w-4 h-4" data-id="element-14818" />
              </div>
              <div data-id="element-14819">
                <h3 className="font-bold text-slate-900" data-id="element-14820">Март 2026 — Прорыв</h3>
                <p className="text-xs text-slate-500" data-id="element-14821">
                  Самый активный месяц года
                </p>
              </div>
            </div>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-14822">
              Март станет поворотным месяцем. Новолуние в вашем 10 доме 15 марта
              запускает новый карьерный цикл. Это идеальное время для запуска
              проектов, переговоров о повышении или смены работы. Юпитер в
              тригоне к натальному Солнцу усиливает вашу уверенность и
              притягивает удачу.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-14823">
              <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-14824">
                <h4 className="font-bold text-emerald-800 mb-3" data-id="element-14825">
                  Благоприятно
                </h4>
                <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-14826">
                  <li className="flex items-start gap-2" data-id="element-14827">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-14828" />
                    Карьерные переговоры
                  </li>
                  <li className="flex items-start gap-2" data-id="element-14829">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-14830" />
                    Запуск проектов
                  </li>
                  <li className="flex items-start gap-2" data-id="element-14831">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-14832" />
                    Публичные выступления
                  </li>
                </ul>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-14833">
                <h4 className="font-bold text-amber-800 mb-3" data-id="element-14834">Осторожно</h4>
                <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-14835">
                  <li className="flex items-start gap-2" data-id="element-14836">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-14837" />
                    Импульсивные решения
                  </li>
                  <li className="flex items-start gap-2" data-id="element-14838">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5" data-id="element-14839" />
                    Переоценка сил
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* ══════════ 15. РЕКОМЕНДАЦИИ НА ГОД ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-amber-50/30 to-white" data-id="element-14840">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-14842" />} title="Рекомендации на год" subtitle="Практические советы по использованию энергий" data-id="element-14841" />

          <div className="space-y-4" data-id="element-14843">
            {SOLAR_RECOMMENDATIONS.map((rec, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-5 border border-amber-200" data-id="element-14844">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-14845">
                  <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold" data-id="element-14846">
                    {i + 1}
                  </span>
                  {rec.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-14847">
                  {rec.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 16. ИТОГ ПРОГНОЗА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14848">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-14850" />} title="Итог прогноза" subtitle="Резюме вашего солярного года" data-id="element-14849" />

          <div className="space-y-4 text-slate-600 leading-relaxed mb-6" data-id="element-14851">
            <p data-id="element-14852">{SOLAR_SUMMARY.overview}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-14853">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-14854">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-14855">
                <TrendingUpIcon className="w-4 h-4" data-id="element-14856" /> Главные возможности
              </h4>
              <p className="text-sm text-emerald-700 leading-relaxed" data-id="element-14857">
                {SOLAR_SUMMARY.opportunities}
              </p>
            </div>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-14858">
              <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2" data-id="element-14859">
                <BookOpenIcon className="w-4 h-4" data-id="element-14860" /> Ключевые уроки
              </h4>
              <p className="text-sm text-indigo-700 leading-relaxed" data-id="element-14861">
                {SOLAR_SUMMARY.lessons}
              </p>
            </div>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER COMPATIBILITY CONTENT
  // ═══════════════════════════════════════════
  const renderCompatibilityContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-14862">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-14863">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-14864">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-14865">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-14866">{subtitle}</p>}
        </div>
      </div>;
    const StrengthsCautions = ({
      strengths,
      cautions,
      strengthsTitle,
      cautionsTitle
    }: {
      strengths: string[];
      cautions: string[];
      strengthsTitle?: string;
      cautionsTitle?: string;
    }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-14867">
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-14868">
          <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-14869">
            <ShieldIcon className="w-4 h-4" data-id="element-14870" />{' '}
            {strengthsTitle || 'Сильные стороны'}
          </h4>
          <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-14871">
            {strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-14872">
                <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-14873" />
                {s}
              </li>)}
          </ul>
        </div>
        <div className="bg-amber-50 rounded-xl p-4 border border-amber-100" data-id="element-14874">
          <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-14875">
            <AlertTriangleIcon className="w-4 h-4" data-id="element-14876" />{' '}
            {cautionsTitle || 'Зоны внимания'}
          </h4>
          <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-14877">
            {cautions.map((c, i) => <li key={i} className="flex items-start gap-2" data-id="element-14878">
                <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-14879" />
                {c}
              </li>)}
          </ul>
        </div>
      </div>;
    const houseIconMap: Record<string, React.ReactNode> = {
      sun: <SunIcon className="w-5 h-5" data-id="element-14880" />,
      moon: <MoonIcon className="w-5 h-5" data-id="element-14881" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-14882" />,
      zap: <ZapIcon className="w-5 h-5" data-id="element-14883" />,
      sparkles: <SparklesIcon className="w-5 h-5" data-id="element-14884" />,
      compass: <CompassIcon className="w-5 h-5" data-id="element-14885" />,
      home: <StarIcon className="w-5 h-5" data-id="element-14886" />
    };
    const houseColorMap: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        iconBg: 'bg-orange-100 text-orange-600'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100 text-purple-600'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        iconBg: 'bg-pink-100 text-pink-600'
      }
    };
    const strengthIconMap: Record<string, React.ReactNode> = {
      heart: <HeartIcon className="w-4 h-4" data-id="element-14887" />,
      sparkles: <SparklesIcon className="w-4 h-4" data-id="element-14888" />,
      zap: <ZapIcon className="w-4 h-4" data-id="element-14889" />,
      home: <StarIcon className="w-4 h-4" data-id="element-14890" />,
      compass: <CompassIcon className="w-4 h-4" data-id="element-14891" />
    };
    const strengthColorMap: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      amber: {
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        iconBg: 'bg-amber-100 text-amber-600'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        iconBg: 'bg-pink-100 text-pink-600'
      },
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        iconBg: 'bg-purple-100 text-purple-600'
      }
    };
    return <>
        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14892">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-14894" />} title={COMPAT_INTRO.title} subtitle="Что такое синастрия и как работает анализ" data-id="element-14893" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14895">
            <p data-id="element-14896">{COMPAT_INTRO.whatIsSynastry}</p>
            <p data-id="element-14897">{COMPAT_INTRO.howItWorks}</p>
            <p data-id="element-14898">{COMPAT_INTRO.whatIsAnalyzed}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ДИНАМИКА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/50 to-white" data-id="element-14899">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-14901" />} title={COMPAT_OVERALL_DYNAMICS.title} subtitle="Энергетика и потенциал вашего союза" data-id="element-14900" />

          {/* Compatibility Scores */}
          <div className="space-y-4 mb-6" data-id="element-14902">
            {COMPATIBILITY_SCORES.map((item, i) => <div key={i} data-id="element-14903">
                <div className="flex justify-between text-sm mb-1.5" data-id="element-14904">
                  <span className="font-medium text-slate-700" data-id="element-14905">
                    {item.area}
                  </span>
                  <span className={`font-bold ${item.percent >= 80 ? 'text-emerald-600' : item.percent >= 60 ? 'text-amber-600' : 'text-rose-600'}`} data-id="element-14906">
                    {item.percent}%
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden" data-id="element-14907">
                  <motion.div className={`${item.color} h-3 rounded-full`} initial={{
                width: 0
              }} animate={{
                width: `${item.percent}%`
              }} transition={{
                duration: 1,
                delay: i * 0.15,
                ease: 'easeOut'
              }} data-id="element-14908" />
                </div>
              </div>)}
          </div>

          <div className="bg-rose-100/50 rounded-xl p-4 border border-rose-200 mb-6" data-id="element-14909">
            <div className="text-center" data-id="element-14910">
              <div className="text-3xl font-bold text-rose-600 mb-1" data-id="element-14911">83%</div>
              <div className="text-sm text-rose-700" data-id="element-14912">
                Общий индекс совместимости
              </div>
            </div>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14913">
            <p data-id="element-14914">{COMPAT_OVERALL_DYNAMICS.overallEnergy}</p>
            <p data-id="element-14915">{COMPAT_OVERALL_DYNAMICS.potential}</p>
          </div>

          <StrengthsCautions strengths={COMPAT_OVERALL_DYNAMICS.strengths} cautions={COMPAT_OVERALL_DYNAMICS.challenges} cautionsTitle="Ключевые вызовы" data-id="element-14916" />
        </div>

        {/* ══════════ 03. СОЛНЦЕ И ЛИЧНОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14917">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-14919" />} title={COMPAT_SUN_INTERACTION.title} subtitle={COMPAT_SUN_INTERACTION.subtitle} data-id="element-14918" />

          {/* Two People's Big Three */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6" data-id="element-14920">
            <div className="bg-rose-50/50 rounded-xl p-4 border border-rose-100" data-id="element-14921">
              <div className="text-sm font-bold text-rose-600 mb-3" data-id="element-14922">
                Алексей
              </div>
              <div className="space-y-2" data-id="element-14923">
                <div className="flex justify-between text-sm" data-id="element-14924">
                  <span className="text-slate-500" data-id="element-14925">☉ Солнце</span>
                  <span className="font-medium text-slate-700" data-id="element-14926">♌ Лев</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-14927">
                  <span className="text-slate-500" data-id="element-14928">☽ Луна</span>
                  <span className="font-medium text-slate-700" data-id="element-14929">
                    ♏ Скорпион
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-14930">
                  <span className="text-slate-500" data-id="element-14931">ASC</span>
                  <span className="font-medium text-slate-700" data-id="element-14932">♐ Стрелец</span>
                </div>
              </div>
            </div>
            <div className="bg-sky-50/50 rounded-xl p-4 border border-sky-100" data-id="element-14933">
              <div className="text-sm font-bold text-sky-600 mb-3" data-id="element-14934">Мария</div>
              <div className="space-y-2" data-id="element-14935">
                <div className="flex justify-between text-sm" data-id="element-14936">
                  <span className="text-slate-500" data-id="element-14937">☉ Солнце</span>
                  <span className="font-medium text-slate-700" data-id="element-14938">♓ Рыбы</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-14939">
                  <span className="text-slate-500" data-id="element-14940">☽ Луна</span>
                  <span className="font-medium text-slate-700" data-id="element-14941">♋ Рак</span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-14942">
                  <span className="text-slate-500" data-id="element-14943">ASC</span>
                  <span className="font-medium text-slate-700" data-id="element-14944">♎ Весы</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14945">
            <p data-id="element-14946">{COMPAT_SUN_INTERACTION.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-14947">
              <Card padding="md" className="bg-white border-amber-200" data-id="element-14948">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-14949">
                  Алексей (♌ Лев)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-14950">
                  {COMPAT_SUN_INTERACTION.partner1Goals}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-14951">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-14952">
                  Мария (♓ Рыбы)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-14953">
                  {COMPAT_SUN_INTERACTION.partner2Goals}
                </p>
              </Card>
            </div>
            <p data-id="element-14954">
              <strong className="text-slate-800" data-id="element-14955">Взаимное понимание:</strong>{' '}
              {COMPAT_SUN_INTERACTION.mutualUnderstanding}
            </p>
          </div>
        </div>

        {/* ══════════ 04. ЭМОЦИОНАЛЬНАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-14956">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-14958" />} title={COMPAT_EMOTIONAL.title} subtitle={COMPAT_EMOTIONAL.subtitle} data-id="element-14957" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14959">
            <p data-id="element-14960">{COMPAT_EMOTIONAL.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-14961">
              <Card padding="md" className="bg-white border-rose-200" data-id="element-14962">
                <h4 className="text-xs font-bold text-rose-600 uppercase tracking-wider mb-2" data-id="element-14963">
                  Алексей (☽ Скорпион)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-14964">
                  {COMPAT_EMOTIONAL.partner1Needs}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-sky-200" data-id="element-14965">
                <h4 className="text-xs font-bold text-sky-600 uppercase tracking-wider mb-2" data-id="element-14966">
                  Мария (☽ Рак)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-14967">
                  {COMPAT_EMOTIONAL.partner2Needs}
                </p>
              </Card>
            </div>

            <p data-id="element-14968">
              <strong className="text-slate-800" data-id="element-14969">
                Эмоциональная поддержка:
              </strong>{' '}
              {COMPAT_EMOTIONAL.emotionalSupport}
            </p>
          </div>

          <StrengthsCautions strengths={COMPAT_EMOTIONAL.strengths} cautions={COMPAT_EMOTIONAL.cautions} data-id="element-14970" />
        </div>

        {/* ══════════ 05. РОМАНТИЧЕСКАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14971">
          <SectionHeader icon={<HeartIcon className="w-5 h-5" data-id="element-14973" />} title={COMPAT_ROMANTIC.title} subtitle={COMPAT_ROMANTIC.subtitle} data-id="element-14972" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14974">
            <p data-id="element-14975">{COMPAT_ROMANTIC.description}</p>
            <p data-id="element-14976">
              <strong className="text-slate-800" data-id="element-14977">Романтическая химия:</strong>{' '}
              {COMPAT_ROMANTIC.chemistry}
            </p>
            <p data-id="element-14978">{COMPAT_ROMANTIC.loveStyles}</p>
          </div>

          <StrengthsCautions strengths={COMPAT_ROMANTIC.strengths} cautions={COMPAT_ROMANTIC.cautions} data-id="element-14979" />
        </div>

        {/* ══════════ 06. ИНТЕЛЛЕКТУАЛЬНАЯ СОВМЕСТИМОСТЬ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-sky-50/30 to-white" data-id="element-14980">
          <SectionHeader icon={<MessageCircleIcon className="w-5 h-5" data-id="element-14982" />} title={COMPAT_INTELLECTUAL.title} subtitle={COMPAT_INTELLECTUAL.subtitle} data-id="element-14981" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-14983">
            <p data-id="element-14984">{COMPAT_INTELLECTUAL.description}</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6" data-id="element-14985">
              <Card padding="md" className="bg-white border-amber-200" data-id="element-14986">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-14987">
                  Стиль Алексея (☿ Лев)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-14988">
                  {COMPAT_INTELLECTUAL.partner1Style}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-indigo-200" data-id="element-14989">
                <h4 className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-14990">
                  Стиль Марии (☿ Рыбы)
                </h4>
                <p className="text-sm text-slate-600" data-id="element-14991">
                  {COMPAT_INTELLECTUAL.partner2Style}
                </p>
              </Card>
            </div>

            <p data-id="element-14992">{COMPAT_INTELLECTUAL.communicationDynamics}</p>
          </div>

          <div className="mt-6 bg-amber-50 rounded-xl p-4 border border-amber-200" data-id="element-14993">
            <h4 className="font-bold text-amber-800 mb-3 flex items-center gap-2" data-id="element-14994">
              <AlertTriangleIcon className="w-4 h-4" data-id="element-14995" /> Возможные сложности
            </h4>
            <ul className="space-y-1.5 text-sm text-amber-700" data-id="element-14996">
              {COMPAT_INTELLECTUAL.difficulties.map((d, i) => <li key={i} className="flex items-start gap-2" data-id="element-14997">
                  <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-14998" />
                  {d}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 07. КАРМИЧЕСКИЕ СВЯЗИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-14999">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-15001" />} title={COMPAT_KARMIC.title} subtitle={COMPAT_KARMIC.subtitle} data-id="element-15000" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-15002">
            {COMPAT_KARMIC.description}
          </p>

          <div className="space-y-6" data-id="element-15003">
            {[COMPAT_KARMIC.saturnAnalysis, COMPAT_KARMIC.plutoAnalysis, COMPAT_KARMIC.nodesAnalysis].map((analysis, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-xl p-5 border border-purple-200" data-id="element-15004">
                <h4 className="font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-15005">
                  <span className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold" data-id="element-15006">
                    {idx + 1}
                  </span>
                  {analysis.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-15007">
                  {analysis.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 08. ПЛАНЕТЫ В ДОМАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-15008">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-15010" />} title="Планеты партнёра в домах другого" subtitle="Как вы влияете на жизнь друг друга" data-id="element-15009" />

          <div className="space-y-4" data-id="element-15011">
            {COMPAT_PLANETS_IN_HOUSES.map((item, idx) => {
            const colors = houseColorMap[item.color] || houseColorMap.amber;
            return <motion.div key={idx} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-15012">
                  <div className="flex items-center gap-3 mb-3" data-id="element-15013">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-15014">
                      {houseIconMap[item.icon] || <StarIcon className="w-4 h-4" data-id="element-15015" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-15016">{item.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-15017">
                    {item.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 09. АСПЕКТЫ МЕЖДУ КАРТАМИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-15018">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-15020" />} title="Основные аспекты между картами" subtitle="Взаимодействие планетарных энергий" data-id="element-15019" />

          <div className="space-y-4" data-id="element-15021">
            {COMPAT_ASPECTS_DETAILED.map((aspect, i) => {
            const isHarmonious = aspect.nature === 'harmonious';
            const isIntense = aspect.nature === 'intense';
            return <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.08
            }} className={`rounded-xl p-5 border ${isHarmonious ? 'bg-emerald-50/50 border-emerald-200' : isIntense ? 'bg-purple-50/50 border-purple-200' : 'bg-rose-50/50 border-rose-200'}`} data-id="element-15022">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-15023">
                    <div className="flex items-center gap-2 sm:gap-3" data-id="element-15024">
                      <span className={`text-lg font-bold ${isHarmonious ? 'text-emerald-600' : isIntense ? 'text-purple-600' : 'text-rose-600'}`} data-id="element-15025">
                        {aspect.aspect}
                      </span>
                      <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-15026">
                        {aspect.title}
                      </h4>
                    </div>
                    <Badge variant={isHarmonious ? 'success' : isIntense ? 'pro' : 'warning'} className="text-xs" data-id="element-15027">
                      {isHarmonious ? 'Гармоничный' : isIntense ? 'Интенсивный' : 'Напряжённый'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-15028">
                    {aspect.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 10. СИЛЬНЫЕ СТОРОНЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-emerald-50/30 to-white" data-id="element-15029">
          <SectionHeader icon={<ShieldIcon className="w-5 h-5" data-id="element-15031" />} title={COMPAT_STRENGTHS.title} subtitle={COMPAT_STRENGTHS.description} data-id="element-15030" />

          <div className="space-y-4" data-id="element-15032">
            {COMPAT_STRENGTHS.items.map((item, idx) => {
            const colors = strengthColorMap[item.color] || strengthColorMap.rose;
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.08
            }} className={`${colors.bg} rounded-xl p-5 border ${colors.border}`} data-id="element-15033">
                  <div className="flex items-center gap-3 mb-3" data-id="element-15034">
                    <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`} data-id="element-15035">
                      {strengthIconMap[item.icon] || <StarIcon className="w-4 h-4" data-id="element-15036" />}
                    </div>
                    <h4 className="font-bold text-slate-900" data-id="element-15037">{item.title}</h4>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed" data-id="element-15038">
                    {item.text}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 11. ВОЗМОЖНЫЕ КОНФЛИКТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-15039">
          <SectionHeader icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-15041" />} title={COMPAT_CONFLICTS.title} subtitle={COMPAT_CONFLICTS.description} data-id="element-15040" />

          <div className="space-y-4" data-id="element-15042">
            {COMPAT_CONFLICTS.items.map((item, idx) => {
            const severityColors = {
              high: {
                bg: 'bg-rose-50',
                border: 'border-rose-200',
                badge: 'warning' as const,
                label: 'Высокий'
              },
              medium: {
                bg: 'bg-amber-50',
                border: 'border-amber-200',
                badge: 'neutral' as const,
                label: 'Средний'
              },
              low: {
                bg: 'bg-slate-50',
                border: 'border-slate-200',
                badge: 'success' as const,
                label: 'Низкий'
              }
            };
            const sev = severityColors[item.severity];
            return <motion.div key={idx} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className={`${sev.bg} rounded-xl p-5 border ${sev.border}`} data-id="element-15043">
                  <div className="flex flex-wrap items-start sm:items-center justify-between gap-2 sm:gap-3 mb-3" data-id="element-15044">
                    <h4 className="font-bold text-slate-900 text-sm sm:text-base" data-id="element-15045">
                      {item.title}
                    </h4>
                    <Badge variant={sev.badge} className="text-xs" data-id="element-15046">
                      Риск: {sev.label}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-15047">
                    {item.text}
                  </p>
                  <div className="bg-white/60 rounded-lg p-3 border border-white/80" data-id="element-15048">
                    <p className="text-xs text-slate-700" data-id="element-15049">
                      <strong className="text-emerald-700" data-id="element-15050">Решение:</strong>{' '}
                      {item.solution}
                    </p>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        {/* ══════════ 12. ДОЛГОСРОЧНЫЙ ПОТЕНЦИАЛ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-purple-50/30 to-white" data-id="element-15051">
          <SectionHeader icon={<TrendingUpIcon className="w-5 h-5" data-id="element-15053" />} title={COMPAT_LONGTERM.title} subtitle={`Общий потенциал: ${COMPAT_LONGTERM.overallScore}%`} data-id="element-15052" />

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-15054">
            <p data-id="element-15055">{COMPAT_LONGTERM.description}</p>
            <p data-id="element-15056">
              <strong className="text-slate-800" data-id="element-15057">Устойчивость союза:</strong>{' '}
              {COMPAT_LONGTERM.sustainability}
            </p>
            <p data-id="element-15058">
              <strong className="text-slate-800" data-id="element-15059">Совместные цели:</strong>{' '}
              {COMPAT_LONGTERM.sharedGoals}
            </p>
          </div>

          <div className="mt-6 bg-purple-100/50 rounded-xl p-4 border border-purple-200" data-id="element-15060">
            <h4 className="font-bold text-purple-800 mb-3" data-id="element-15061">
              Направления развития отношений
            </h4>
            <ul className="space-y-1.5 text-sm text-purple-700" data-id="element-15062">
              {COMPAT_LONGTERM.developmentAreas.map((area, i) => <li key={i} className="flex items-start gap-2" data-id="element-15063">
                  <TrendingUpIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-15064" />
                  {area}
                </li>)}
            </ul>
          </div>
        </div>

        {/* ══════════ 13. РЕКОМЕНДАЦИИ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-15065">
          <SectionHeader icon={<TargetIcon className="w-5 h-5" data-id="element-15067" />} title="Практические рекомендации" subtitle="Советы для укрепления отношений" data-id="element-15066" />

          <div className="space-y-4" data-id="element-15068">
            {COMPAT_RECOMMENDATIONS.map((rec, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-5 border border-rose-200" data-id="element-15069">
                <h4 className="font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-15070">
                  <span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-xs font-bold" data-id="element-15071">
                    {i + 1}
                  </span>
                  {rec.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed" data-id="element-15072">
                  {rec.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 14. ИТОГ АНАЛИЗА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-rose-50/30 to-white" data-id="element-15073">
          <SectionHeader icon={<CompassIcon className="w-5 h-5" data-id="element-15075" />} title="Итог анализа" subtitle="Резюме совместимости" data-id="element-15074" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-15076">
            {COMPAT_SUMMARY.overview}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-15077">
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-100" data-id="element-15078">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-15079">
                <CheckIcon className="w-4 h-4" data-id="element-15080" /> Главные плюсы
              </h4>
              <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-15081">
                {COMPAT_SUMMARY.mainPros.map((p, i) => <li key={i} className="flex items-start gap-2" data-id="element-15082">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-15083" />
                    {p}
                  </li>)}
              </ul>
            </div>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-15084">
              <h4 className="font-bold text-indigo-800 mb-3 flex items-center gap-2" data-id="element-15085">
                <BookOpenIcon className="w-4 h-4" data-id="element-15086" /> Ключевые уроки
              </h4>
              <ul className="space-y-1.5 text-sm text-indigo-700" data-id="element-15087">
                {COMPAT_SUMMARY.keyLessons.map((l, i) => <li key={i} className="flex items-start gap-2" data-id="element-15088">
                    <CompassIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-15089" />
                    {l}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </>;
  };
  // ═══════════════════════════════════════════
  // RENDER COMBO CONTENT
  // ═══════════════════════════════════════════
  const renderComboContent = () => {
    const SectionHeader = ({
      icon,
      title,
      subtitle,
      color = 'purple'
    }: {
      icon: React.ReactNode;
      title: string;
      subtitle?: string;
      color?: string;
    }) => <div className="flex items-center gap-3 mb-4 sm:mb-6" data-id="element-15090">
        <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-${color}-100 text-${color}-600 flex items-center justify-center`} data-id="element-15091">
          {icon}
        </div>
        <div className="min-w-0" data-id="element-15092">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 leading-tight" data-id="element-15093">
            {title}
          </h2>
          {subtitle && <p className="text-xs sm:text-sm text-slate-500" data-id="element-15094">{subtitle}</p>}
        </div>
      </div>;
    const PartDivider = ({
      part,
      title,
      subtitle
    }: {
      part: number;
      title: string;
      subtitle: string;
    }) => <div className={`p-4 sm:p-6 md:p-8 border-b border-slate-100 ${part === 1 ? 'bg-gradient-to-r from-indigo-600 to-purple-600' : 'bg-gradient-to-r from-amber-600 to-orange-600'} text-white`} data-id="element-15095">
        <div className="flex items-center gap-3 sm:gap-4" data-id="element-15096">
          <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-white/20 flex items-center justify-center text-xl sm:text-2xl font-bold flex-shrink-0" data-id="element-15097">
            {part}
          </div>
          <div className="min-w-0" data-id="element-15098">
            <h2 className="text-lg sm:text-2xl font-bold" data-id="element-15099">{title}</h2>
            <p className="text-white/70 text-xs sm:text-sm" data-id="element-15100">{subtitle}</p>
          </div>
        </div>
      </div>;
    const lifeAreaIcons: Record<string, React.ReactNode> = {
      briefcase: <BriefcaseIcon className="w-5 h-5" data-id="element-15101" />,
      heart: <HeartIcon className="w-5 h-5" data-id="element-15102" />,
      'trending-up': <TrendingUpIcon className="w-5 h-5" data-id="element-15103" />,
      compass: <CompassIcon className="w-5 h-5" data-id="element-15104" />,
      users: <UsersIcon className="w-5 h-5" data-id="element-15105" />,
      activity: <ActivityIcon className="w-5 h-5" data-id="element-15106" />
    };
    const lifeAreaColors: Record<string, {
      bg: string;
      border: string;
      iconBg: string;
    }> = {
      emerald: {
        bg: 'bg-emerald-50',
        border: 'border-emerald-200',
        iconBg: 'bg-emerald-100 text-emerald-600'
      },
      rose: {
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        iconBg: 'bg-rose-100 text-rose-600'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        iconBg: 'bg-indigo-100 text-indigo-600'
      },
      teal: {
        bg: 'bg-teal-50',
        border: 'border-teal-200',
        iconBg: 'bg-teal-100 text-teal-600'
      },
      sky: {
        bg: 'bg-sky-50',
        border: 'border-sky-200',
        iconBg: 'bg-sky-100 text-sky-600'
      }
    };
    return <>
        {/* ═══════════════════════════════════ */}
        {/* PART 1 — NATAL CHART */}
        {/* ═══════════════════════════════════ */}
        <PartDivider part={1} title="Натальная карта" subtitle="Анализ личности, потенциала и жизненных особенностей" data-id="element-15107" />

        {/* ══════════ 01. ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-15108">
          <SectionHeader icon={<BookOpenIcon className="w-5 h-5" data-id="element-15110" />} title={COMBO_NATAL_INTRO.title} subtitle="Что такое натальная карта и что она показывает" color="indigo" data-id="element-15109" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-15111">
            <p data-id="element-15112">{COMBO_NATAL_INTRO.whatIsNatalChart}</p>
            <p data-id="element-15113">{COMBO_NATAL_INTRO.whatItShows}</p>
            <p data-id="element-15114">{COMBO_NATAL_INTRO.whatIsAnalyzed}</p>
          </div>
        </div>

        {/* ══════════ 02. ОБЩАЯ ХАРАКТЕРИСТИКА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/50 to-white" data-id="element-15115">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-15117" />} title={COMBO_PERSONALITY.title} subtitle="Базовые черты, мотивации и ценности" color="indigo" data-id="element-15116" />

          <p className="text-slate-600 leading-relaxed mb-6" data-id="element-15118">
            {COMBO_PERSONALITY.overview}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-id="element-15119">
            {COMBO_PERSONALITY.traits.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} className="bg-white rounded-xl p-4 border border-indigo-200" data-id="element-15120">
                <h4 className="font-bold text-indigo-800 mb-2" data-id="element-15121">{item.trait}</h4>
                <p className="text-sm text-slate-600" data-id="element-15122">{item.description}</p>
              </motion.div>)}
          </div>

          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-15123">
            <p data-id="element-15124">
              <strong className="text-slate-800" data-id="element-15125">Мотивации:</strong>{' '}
              {COMBO_PERSONALITY.motivations}
            </p>
            <p data-id="element-15126">
              <strong className="text-slate-800" data-id="element-15127">Ценности:</strong>{' '}
              {COMBO_PERSONALITY.values}
            </p>
          </div>
        </div>

        {/* ══════════ 03. СОЛНЦЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-15128">
          <SectionHeader icon={<SunIcon className="w-5 h-5" data-id="element-15130" />} title={COMBO_SUN.title} subtitle={COMBO_SUN.subtitle} color="amber" data-id="element-15129" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-15131">
            <p data-id="element-15132">{COMBO_SUN.description}</p>
            <p data-id="element-15133">
              <strong className="text-slate-800" data-id="element-15134">Жизненные цели:</strong>{' '}
              {COMBO_SUN.lifeGoals}
            </p>
            <p data-id="element-15135">
              <strong className="text-slate-800" data-id="element-15136">Проявление личности:</strong>{' '}
              {COMBO_SUN.personality}
            </p>
            <p data-id="element-15137">
              <strong className="text-slate-800" data-id="element-15138">Внутренний потенциал:</strong>{' '}
              {COMBO_SUN.potential}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6" data-id="element-15139">
            <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100" data-id="element-15140">
              <h4 className="font-bold text-emerald-800 mb-3 flex items-center gap-2" data-id="element-15141">
                <ShieldIcon className="w-4 h-4" data-id="element-15142" /> Суперсилы
              </h4>
              <ul className="space-y-1.5 text-sm text-emerald-700" data-id="element-15143">
                {COMBO_SUN.strengths.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-15144">
                    <CheckIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-15145" />
                    {s}
                  </li>)}
              </ul>
            </div>
            <div className="bg-rose-50 rounded-xl p-4 border border-rose-100" data-id="element-15146">
              <h4 className="font-bold text-rose-800 mb-3 flex items-center gap-2" data-id="element-15147">
                <ZapIcon className="w-4 h-4" data-id="element-15148" /> Теневая сторона
              </h4>
              <ul className="space-y-1.5 text-sm text-rose-700" data-id="element-15149">
                {COMBO_SUN.shadows.map((s, i) => <li key={i} className="flex items-start gap-2" data-id="element-15150">
                    <AlertTriangleIcon className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" data-id="element-15151" />
                    {s}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>

        {/* ══════════ 04. ЛУНА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-indigo-50/30 to-white" data-id="element-15152">
          <SectionHeader icon={<MoonIcon className="w-5 h-5" data-id="element-15154" />} title={COMBO_MOON.title} subtitle={COMBO_MOON.subtitle} color="indigo" data-id="element-15153" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-15155">
            <p data-id="element-15156">{COMBO_MOON.description}</p>
            <p data-id="element-15157">
              <strong className="text-slate-800" data-id="element-15158">Эмоциональные реакции:</strong>{' '}
              {COMBO_MOON.emotionalReactions}
            </p>
            <p data-id="element-15159">
              <strong className="text-slate-800" data-id="element-15160">
                Внутренние потребности:
              </strong>{' '}
              {COMBO_MOON.innerNeeds}
            </p>
            <p data-id="element-15161">
              <strong className="text-slate-800" data-id="element-15162">
                Способ переживания чувств:
              </strong>{' '}
              {COMBO_MOON.feelingStyle}
            </p>
          </div>
        </div>

        {/* ══════════ 05. АСЦЕНДЕНТ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-15163">
          <SectionHeader icon={<StarIcon className="w-5 h-5" data-id="element-15165" />} title={COMBO_ASCENDANT.title} subtitle={COMBO_ASCENDANT.subtitle} color="purple" data-id="element-15164" />
          <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-15166">
            <p data-id="element-15167">{COMBO_ASCENDANT.description}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 my-6" data-id="element-15168">
              <Card padding="md" className="bg-white border-purple-200" data-id="element-15169">
                <h4 className="text-xs font-bold text-purple-600 uppercase tracking-wider mb-2" data-id="element-15170">
                  Внешнее проявление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-15171">
                  {COMBO_ASCENDANT.externalPresentation}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-amber-200" data-id="element-15172">
                <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-15173">
                  Первое впечатление
                </h4>
                <p className="text-sm text-slate-600" data-id="element-15174">
                  {COMBO_ASCENDANT.firstImpression}
                </p>
              </Card>
              <Card padding="md" className="bg-white border-emerald-200" data-id="element-15175">
                <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2" data-id="element-15176">
                  Стиль поведения
                </h4>
                <p className="text-sm text-slate-600" data-id="element-15177">
                  {COMBO_ASCENDANT.behaviorStyle}
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* ══════════ 06. ПЛАНЕТЫ В ЗНАКАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100 bg-gradient-to-br from-purple-50/30 to-white" data-id="element-15178">
          <SectionHeader icon={<SparklesIcon className="w-5 h-5" data-id="element-15180" />} title="Планеты в знаках" subtitle="Влияние каждой планеты на вашу личность" color="purple" data-id="element-15179" />
          <div className="space-y-4" data-id="element-15181">
            {COMBO_PLANETS_IN_SIGNS.map((planet, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.06
          }} className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-15182">
                <div className="flex items-center gap-3 mb-3" data-id="element-15183">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center text-lg" data-id="element-15184">
                    {planet.symbol}
                  </div>
                  <div data-id="element-15185">
                    <h4 className="font-bold text-slate-900" data-id="element-15186">
                      {planet.planet} в {planet.sign}
                    </h4>
                    <p className="text-xs text-slate-500" data-id="element-15187">
                      {planet.house}-й дом
                    </p>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-3" data-id="element-15188">
                  {planet.description}
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-15189">
                  {planet.strengths.map((s, i) => <span key={i} className="text-xs bg-purple-50 text-purple-700 px-2.5 py-1 rounded-full border border-purple-100" data-id="element-15190">
                      {s}
                    </span>)}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 07. ПЛАНЕТЫ В ДОМАХ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 border-b border-slate-100" data-id="element-15191">
          <SectionHeader icon={<LayersIcon className="w-5 h-5" data-id="element-15193" />} title="Планеты в домах" subtitle="Активированные сферы жизни" color="indigo" data-id="element-15192" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-15194">
            {COMBO_HOUSES_SUMMARY.map((house, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.04
          }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-15195">
                <div className="flex items-center gap-2 mb-2" data-id="element-15196">
                  <span className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold" data-id="element-15197">
                    {house.house}
                  </span>
                  <div data-id="element-15198">
                    <span className="text-sm font-bold text-slate-900" data-id="element-15199">
                      {house.theme}
                    </span>
                    <span className="text-xs text-slate-400 ml-2" data-id="element-15200">
                      {house.sign}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-slate-600" data-id="element-15201">{house.text}</p>
              </motion.div>)}
          </div>
        </div>

        {/* ══════════ 08. АСПЕКТЫ НАТАЛЬНОЙ КАРТЫ (COMBO) ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-15202">
          <div className="max-w-5xl mx-auto" data-id="element-15203">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-15204">
              <SparklesIcon className="w-5 h-5 text-indigo-500" data-id="element-15205" />
              Аспекты натальной карты
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-15206">
              {COMBO_NATAL_ASPECTS.map((aspect, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.03
            }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-15207">
                  <div className="flex items-center gap-2 mb-2" data-id="element-15208">
                    <span className="text-sm font-bold text-slate-900" data-id="element-15209">
                      {aspect.aspect}
                    </span>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-15210">
                      {aspect.type}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600" data-id="element-15211">{aspect.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 09. ЖИЗНЕННЫЕ СФЕРЫ (COMBO) ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-15212">
          <div className="max-w-5xl mx-auto" data-id="element-15213">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-15214">
              <TargetIcon className="w-5 h-5 text-emerald-500" data-id="element-15215" />
              Жизненные сферы
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-15216">
              {COMBO_NATAL_LIFE_AREAS.map((area, i) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: i * 0.05
            }} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm" data-id="element-15217">
                  <h4 className="text-sm font-bold text-slate-900 mb-2" data-id="element-15218">
                    {area.area}
                  </h4>
                  <p className="text-xs text-slate-600 leading-relaxed" data-id="element-15219">
                    {area.text}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 10. СОЛЯР — ВВЕДЕНИЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t-4 border-amber-400" data-id="element-15220">
          <div className="max-w-5xl mx-auto text-center" data-id="element-15221">
            <Badge variant="warning" size="sm" className="mb-3" data-id="element-15222">
              Часть 2
            </Badge>
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-15223">
              Солярный возврат
            </h2>
            <p className="text-sm text-slate-500 max-w-2xl mx-auto" data-id="element-15224">
              {COMBO_SOLAR_INTRO}
            </p>
          </div>
        </div>

        {/* ══════════ 11. ЭНЕРГИЯ ГОДА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-15225">
          <div className="max-w-5xl mx-auto" data-id="element-15226">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-15227">
              <SunIcon className="w-5 h-5 text-amber-500" data-id="element-15228" />
              Энергия года
            </h3>
            <div className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-15229">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-15230">
                {COMBO_YEAR_ENERGY}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 12. СОЛЯР — АСЦЕНДЕНТ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-15231">
          <div className="max-w-5xl mx-auto" data-id="element-15232">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-15233">
              <CompassIcon className="w-5 h-5 text-indigo-500" data-id="element-15234" />
              Асцендент соляра
            </h3>
            <div className="bg-indigo-50 rounded-xl p-5 border border-indigo-100" data-id="element-15235">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-15236">
                {COMBO_SOLAR_ASCENDANT}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 13. СОЛЯР — СОЛНЦЕ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-15237">
          <div className="max-w-5xl mx-auto" data-id="element-15238">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-15239">
              <SunIcon className="w-5 h-5 text-amber-500" data-id="element-15240" />
              Солнце в соляре
            </h3>
            <div className="bg-white rounded-xl p-5 border border-slate-200" data-id="element-15241">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-15242">
                {COMBO_SOLAR_SUN}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 14. СОЛЯР — ЛУНА ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-15243">
          <div className="max-w-5xl mx-auto" data-id="element-15244">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-15245">
              <MoonIcon className="w-5 h-5 text-blue-500" data-id="element-15246" />
              Луна в соляре
            </h3>
            <div className="bg-blue-50 rounded-xl p-5 border border-blue-100" data-id="element-15247">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-15248">
                {COMBO_SOLAR_MOON}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ 15. СОЛЯР — ПЛАНЕТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-15249">
          <div className="max-w-5xl mx-auto" data-id="element-15250">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-15251">
              <StarIcon className="w-5 h-5 text-purple-500" data-id="element-15252" />
              Планеты в соляре
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-15253">
              {COMBO_SOLAR_PLANETS.map((planet, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.04
            }} className="bg-white rounded-xl p-4 border border-slate-200" data-id="element-15254">
                  <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-15255">
                    {planet.planet}
                  </h4>
                  <p className="text-[10px] text-slate-400 mb-2" data-id="element-15256">
                    {planet.position}
                  </p>
                  <p className="text-xs text-slate-600" data-id="element-15257">{planet.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 16. СОЛЯР — АСПЕКТЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-15258">
          <div className="max-w-5xl mx-auto" data-id="element-15259">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-15260">
              <ActivityIcon className="w-5 h-5 text-rose-500" data-id="element-15261" />
              Аспекты соляра
            </h3>
            <div className="space-y-3" data-id="element-15262">
              {COMBO_SOLAR_ASPECTS.map((aspect, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.03
            }} className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-id="element-15263">
                  <div className="flex items-center gap-2 mb-1" data-id="element-15264">
                    <span className="text-sm font-bold text-slate-900" data-id="element-15265">
                      {aspect.aspect}
                    </span>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-15266">
                      {aspect.type}
                    </Badge>
                  </div>
                  <p className="text-xs text-slate-600" data-id="element-15267">{aspect.text}</p>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 17. КЛЮЧЕВЫЕ ПЕРИОДЫ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-slate-50 border-t border-slate-200" data-id="element-15268">
          <div className="max-w-5xl mx-auto" data-id="element-15269">
            <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-15270">
              <CalendarIcon className="w-5 h-5 text-emerald-500" data-id="element-15271" />
              Ключевые периоды года
            </h3>
            <div className="space-y-3" data-id="element-15272">
              {COMBO_KEY_PERIODS.map((period, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.05
            }} className="bg-white rounded-xl p-4 border border-slate-200 flex gap-4" data-id="element-15273">
                  <div className="w-16 shrink-0 text-center" data-id="element-15274">
                    <div className="text-xs font-bold text-indigo-600 bg-indigo-50 rounded-lg px-2 py-1" data-id="element-15275">
                      {period.period}
                    </div>
                  </div>
                  <div data-id="element-15276">
                    <h4 className="text-sm font-bold text-slate-900 mb-1" data-id="element-15277">
                      {period.theme}
                    </h4>
                    <p className="text-xs text-slate-600" data-id="element-15278">{period.text}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>

        {/* ══════════ 18. ВЗАИМОДЕЙСТВИЕ СОЛЯР + НАТАЛ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-white border-t border-slate-200" data-id="element-15279">
          <div className="max-w-5xl mx-auto" data-id="element-15280">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-15281">
              <LayersIcon className="w-5 h-5 text-purple-500" data-id="element-15282" />
              Взаимодействие соляра и натала
            </h3>
            <div className="bg-purple-50 rounded-xl p-5 border border-purple-100" data-id="element-15283">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-15284">
                {COMBO_SOLAR_NATAL_INTERACTION}
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ ИТОГОВЫЙ АНАЛИЗ ══════════ */}
        <div className="p-4 sm:p-6 md:p-8 bg-gradient-to-b from-indigo-50 to-white border-t border-indigo-100" data-id="element-15285">
          <div className="max-w-5xl mx-auto text-center" data-id="element-15286">
            <SparklesIcon className="w-8 h-8 text-indigo-500 mx-auto mb-3" data-id="element-15287" />
            <h2 className="text-xl font-bold text-slate-900 mb-4" data-id="element-15288">
              Итоговый анализ
            </h2>
            <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm text-left" data-id="element-15289">
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-15290">
                Ваш комбо-анализ объединяет натальную карту и солярный возврат,
                создавая целостную картину вашей личности и предстоящего года.
                Используйте эти знания как компас для принятия решений и
                раскрытия своего потенциала.
              </p>
            </div>
          </div>
        </div>

        {/* ══════════ FOOTER ══════════ */}
        <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-200" data-id="element-15291">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 max-w-5xl mx-auto" data-id="element-15292">
            <div className="text-center sm:text-left" data-id="element-15293">
              <p className="text-sm font-medium text-slate-700" data-id="element-15294">
                AstroGuide • Premium отчёт
              </p>
              <p className="text-xs text-slate-500 mt-1" data-id="element-15295">
                Отчёт сгенерирован {new Date().toLocaleDateString('ru-RU')} •
                Все права защищены
              </p>
            </div>
            <button onClick={handlePrint} className="report-print-btn flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 hover:text-slate-800 transition-colors text-sm font-medium" data-id="element-15296">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-15297">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" data-id="element-15298" />
                <polyline points="7 10 12 15 17 10" data-id="element-15299" />
                <line x1="12" y1="15" x2="12" y2="3" data-id="element-15300" />
              </svg>
              Скачать PDF
            </button>
          </div>
        </div>

        {/* ══════════ CTA BLOCK ══════════ */}
        <div className="p-6 sm:p-8 md:p-10 bg-white border-t border-slate-200 text-center print:hidden" data-id="element-15301">
          <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3" data-id="element-15302">
            Хотите персональный разбор от профессионала?
          </h3>
          <p className="text-sm sm:text-base text-slate-500 mb-6 max-w-lg mx-auto" data-id="element-15303">
            Закажите индивидуальную консультацию с опытным астрологом для
            глубокого анализа вашей карты
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3" data-id="element-15304">
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium transition-colors" data-id="element-15305">
              <UsersIcon className="w-4 h-4" data-id="element-15306" />
              Каталог астрологов
            </button>
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition-colors shadow-sm" data-id="element-15307">
              Заказать консультацию
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-15308">
                <line x1="5" y1="12" x2="19" y2="12" data-id="element-15309" />
                <polyline points="12 5 19 12 12 19" data-id="element-15310" />
              </svg>
            </button>
          </div>
        </div>

        {/* ══════════ REVIEW SECTION ══════════ */}
        <div className="p-6 sm:p-8 md:p-10 bg-slate-50 border-t border-slate-200 print:hidden" data-id="element-15311">
          <div className="max-w-lg mx-auto text-center" data-id="element-15312">
            {!reviewSubmitted ? <div data-id="element-15313">
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4" data-id="element-15314">
                  <SparklesIcon className="w-6 h-6 text-amber-500" data-id="element-15315" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-15316">
                  Как вам отчёт?
                </h3>
                <p className="text-sm text-slate-500 mb-6" data-id="element-15317">
                  Ваш отзыв поможет улучшить качество наших отчётов
                </p>

                {/* Star Rating */}
                <div className="flex items-center justify-center gap-2 mb-6" data-id="element-15318">
                  {[1, 2, 3, 4, 5].map(star => <button key={star} onClick={() => setReviewRating(star)} className="transition-transform hover:scale-110" data-id="element-15319">
                      <StarIcon className={`w-8 h-8 transition-colors ${star <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-15320" />
                    </button>)}
                </div>

                {reviewRating > 0 && <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-4" data-id="element-15321">
                    {/* Quick Tags */}
                    <div className="flex flex-wrap justify-center gap-2" data-id="element-15322">
                      {['Точность', 'Глубина', 'Оформление', 'Практичность', 'Персонализация', 'Читаемость'].map(tag => <button key={tag} onClick={() => setReviewTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${reviewTags.includes(tag) ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'}`} data-id="element-15323">
                          {tag}
                        </button>)}
                    </div>

                    {/* Text Area */}
                    <textarea value={reviewText} onChange={e => setReviewText(e.target.value)} placeholder="Расскажите подробнее (необязательно)" rows={3} className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 resize-none" data-id="element-15324" />

                    {/* Submit */}
                    <button onClick={() => setReviewSubmitted(true)} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-medium transition-colors shadow-sm" data-id="element-15325">
                      Отправить отзыв
                    </button>

                    <p className="text-xs text-slate-400 mt-2" data-id="element-15326">
                      💎 Оставьте отзыв и получите скидку 10% на следующий отчёт
                    </p>
                  </motion.div>}
              </div> : <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} data-id="element-15327">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4" data-id="element-15328">
                  <CheckIcon className="w-6 h-6 text-emerald-600" data-id="element-15329" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-15330">
                  Спасибо за ваш отзыв!
                </h3>
                <p className="text-sm text-slate-500 mb-4" data-id="element-15331">
                  Скидка 10% уже применена к вашему аккаунту
                </p>
                <div className="flex items-center justify-center gap-1" data-id="element-15332">
                  {[1, 2, 3, 4, 5].map(star => <StarIcon key={star} className={`w-5 h-5 ${star <= reviewRating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-15333" />)}
                </div>
              </motion.div>}
          </div>
        </div>
      </>;
  };
}